import { ruleImpl } from "./rule-impl";
import { issueSeverity, repairLine, worded } from "./rule-catalog";
import type { RuleCode } from "./rule-codes.generated";
import { normalText } from "./normal-text";
import {
  STUDY_OBJECTIVE_MAX_BULLETS,
  STUDY_OBJECTIVE_MAX_CHARS,
} from "./index";
import type { QuestionnaireDraft, QuestionnaireValidationIssue } from "./index";
import { carriedSourceOptions } from "./carried-choices";
import { assetComparisonCoherenceIssues } from "./asset-comparison-coherence";
import { exposureCoherenceIssues } from "./exposure-coherence";
import { canonicalJson } from "./question-dependents";
import { recalledReadIssues, testedAssetUnusedIssues } from "./tested-asset-coverage";
import { answerabilityIssues } from "./answerability";
import {
  isSurveyDataOnlyQuestion,
  reportingCoherenceIssues,
} from "./reporting-coherence";
import {
  MAX_ASSET_DESCRIPTION_WORDS,
  isWithinAssetDescriptionWordLimit,
} from "./asset-description-limits";
import {
  DESIGN_BRIEF_LINE_MAX_CHARS,
  DESIGN_BRIEF_METHOD_MAX_CHARS,
  MAX_QUESTION_LABEL_LENGTH,
  STUDY_NAME_MAX_CHARS,
  STUDY_OBJECTIVE_MAX_CHARS as SITUATION_MAX_CHARS,
  textLength,
} from "./text-limits";
import { keyIdentity } from "./identity";
import { ROUTING_ACTIONS, ROUTING_DISPOSITIONS, ROUTING_SCOPES } from "./routing-vocabulary";
import { requiredRefusalLine } from "./refusal-copy";
import { readLiftTag } from "./lift-tags";
import { recipeDeclarations, studySpecOf, type StudySpec } from "./study-spec";
import { renderAssetRefTokens } from "./asset-ref-tokens";
import {
  DEFAULT_RESEARCH_QUESTION_FLOOR,
  MATRIX_MAX_COLUMNS,
  MATRIX_ROWS_FLOOR,
  MIN_CHOICE_MEMBERS,
  QUESTION_CAPABILITIES,
  RANKED_ITEMS_FLOOR,
  RESEARCH_QUESTION_CEILING,
  SELECT_QUESTION_TYPES,
} from "./question-capabilities";
import { openEndClassifierMetricId } from "./open-end-classifier";
import { isQualitativeSlideType } from "./canonical-vocabularies";
import {
  YOUTUBE_NON_KEYFRAMEABLE_MESSAGE,
  nonKeyframeableVideoMessage,
} from "./video-url";
import {
  ASSET_FAMILY_FACTS,
  DESCRIPTION_REQUIRED_FAMILIES,
  assetContentSupplied,
  isAssetFamily,
} from "./asset-families";
import { respondentTextGluesQuestionStems } from "./question-stem-guard";
import {
  recipeModuleTypeMismatchOf,
} from "./recipe-module-question-type";
import {
  DURATION_EXPOSED_UNITS,
  getUnitRegistryEntry,
  isContinuousUnitId,
  QUESTION_TYPE_IDS,
  isRatingUnitId,
  ratingBounds,
  ratingHasMidpoint,
  unitRequiresExplicitBoundsForComposite,
} from "./registry";
import { isWholeNumberUnit } from "./vocabularies";
import {
  POINT_LABEL_RATING_UNIT_IDS,
  RATING_SCALES,
  SUPPORTED_RATING_UNIT_IDS,
  englishList,
} from "./rating-scales";
import { exactChoiceAssetRefAnswerLabel } from "./asset-items";
import type { PriceWording } from "./product-version-label";
import { respondentWordingFor } from "./scale-wording-packs";
import {
  CONDITION_FIELDS,
  CONDITION_OPERATOR_FAMILIES,
  CONDITION_SOURCE_FIELDS,
  MAX_VISIBILITY_RULE_GROUP_SIZE,
  MIN_VISIBILITY_RULE_GROUP_SIZE,
  NUMERIC_CONDITION_OPERATORS,
  conditionOperatorFamily,
} from "./condition-operators";
import type { ConditionOperatorFamily } from "./condition-operators";
import {
  assetNamesTokenized,
  assetTokensKnown,
  conditionFieldsRead,
  countWithinBounds,
  fieldAllowedForType,
  operatorReadsSource,
  scaleExecutable,
  valueInList,
} from "./rule-evaluators";
import type { RuleFailure } from "./rule-evaluators";

/**
 * How many items a ranking asks for (decision O2): the one typed ranking
 * shape. Every reader of a ranking's bounds reads it: the compiler (with the
 * question's `required`, `minimumCountFields`), the bottom-rank condition
 * (`rankingFixedLength`), the reply facts, and through the compiled survey
 * the twin engine (`survey_engine/ranking_constraints.py`). Python's analysis
 * reads its mirror, `models/questionnaire/ranking_bounds.py`; the two are held
 * to one table (`tests/fixtures/ranking-bounds/variants.json`).
 *
 * With no stated bound it is full: the respondent ranks every item offered,
 * compiled as a plain SurveyJS ranking. So is a ranking of its own fixed items
 * whose bounds are exactly all of them. A maximum alone asks for exactly that
 * many (the top N); a stated minimum is kept as authored, up to the stated
 * maximum or every item. A list carried forward keeps its stated bounds: the
 * items it offers vary by respondent, so its minimum stays a count the source
 * must be able to fill.
 */
export type RankingBounds =
  | { kind: "full" }
  | { kind: "partial"; min: number; max: number };

/**
 * The one range a ranking's stated counts (`minRankedItems`,
 * `maxRankedItems`) hold: each absent, or a whole number from the registry's
 * floor (`RANKED_ITEMS_FLOOR`) to the ranking's option count, the minimum at
 * most the maximum. The write gate refuses a ranking outside it
 * (`rankingCountIssues`); the editor holds an edit inside it
 * (`rankingCountsHeld`) and draws its inputs from it. Python's draft contract
 * states the same floor from the same registry key.
 */
export function rankingCountRange(response: JsonObject): {
  floor: number;
  ceiling: number;
} {
  return {
    floor: RANKED_ITEMS_FLOOR,
    ceiling: asArray(response.options).length,
  };
}

export type RankingCountField = "minRankedItems" | "maxRankedItems";

/** A stated count outside its range, or a minimum above the maximum. */
export type RankingCountIssue = {
  field: RankingCountField;
  rule: "range" | "order";
};

export function rankingCountIssues(
  response: JsonObject,
): Array<RankingCountIssue> {
  const { floor, ceiling } = rankingCountRange(response);
  const issues: Array<RankingCountIssue> = [];
  for (const field of ["minRankedItems", "maxRankedItems"] as const) {
    const value = response[field];
    if (
      typeof value === "number" &&
      (!Number.isInteger(value) || value < floor || value > ceiling)
    ) {
      issues.push({ field, rule: "range" });
    }
  }
  const { minRankedItems: min, maxRankedItems: max } = response;
  if (typeof min === "number" && typeof max === "number" && min > max) {
    issues.push({ field: "minRankedItems", rule: "order" });
  }
  return issues;
}

/**
 * The nearest stated counts inside the range: the maximum first, then the
 * minimum at most it. Only the counts the ranking states; an absent one stays
 * absent. A ranking with no options holds 0, which the gate refuses: the
 * editor keeps the shape, never throws.
 */
export function rankingCountsHeld(
  response: JsonObject,
): Partial<Record<RankingCountField, number>> {
  const { floor, ceiling } = rankingCountRange(response);
  const held: Partial<Record<RankingCountField, number>> = {};
  const { minRankedItems: min, maxRankedItems: max } = response;
  if (typeof max === "number") {
    held.maxRankedItems = Math.min(Math.max(max, floor), ceiling);
  }
  if (typeof min === "number") {
    held.minRankedItems = Math.min(
      Math.max(min, floor),
      held.maxRankedItems ?? ceiling,
    );
  }
  return held;
}

export type SelectionCountField = "minSelected" | "maxSelected";

/**
 * A multi-select's stated counts (`minSelected`, `maxSelected`) held within
 * its option count: the maximum first (1 to the count), then the minimum (1
 * to the maximum). Only the counts it states; an absent one stays absent. The
 * editor holds every edit with it, and the asset-removal cleanup holds a list
 * it shortens (`projectAssetRemoval`), as `rankingCountsHeld` does for a
 * ranking. An empty list holds 0, which the gate refuses.
 */
export function selectionCountsHeld(
  response: JsonObject,
): Partial<Record<SelectionCountField, number>> {
  const ceiling = asArray(response.options).length;
  const held: Partial<Record<SelectionCountField, number>> = {};
  const { minSelected: min, maxSelected: max } = response;
  if (typeof max === "number") {
    held.maxSelected = Math.min(Math.max(max, 1), ceiling);
  }
  if (typeof min === "number") {
    held.minSelected = Math.min(Math.max(min, 1), held.maxSelected ?? ceiling);
  }
  return held;
}

export function rankingBounds(response: JsonObject): RankingBounds {
  const stated = (value: unknown): number | undefined =>
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= RANKED_ITEMS_FLOOR
      ? value
      : undefined;
  const min = stated(response.minRankedItems);
  const max = stated(response.maxRankedItems);
  if (min === undefined && max === undefined) {
    return { kind: "full" };
  }
  const itemCount = asArray(response.options).length;
  const upper = max ?? itemCount;
  const lower = min ?? upper;
  const everyOwnItem =
    !isRecord(response.choicesFrom) &&
    itemCount > 0 &&
    lower === itemCount &&
    upper === itemCount;
  return everyOwnItem
    ? { kind: "full" }
    : { kind: "partial", min: lower, max: upper };
}

/** The most items a respondent can rank: every item, or the stated maximum. */
export function rankingMaxLength(response: JsonObject): number {
  const bounds = rankingBounds(response);
  return bounds.kind === "full" ? asArray(response.options).length : bounds.max;
}

/**
 * How many items every respondent ranks, when that is one number: a full
 * ranking of a fixed list, or a partial one whose minimum is its maximum.
 * Null when it varies (a range, or a list carried forward from what each
 * respondent selected), where the bottom positions hold different items.
 */
export function rankingFixedLength(response: JsonObject): number | null {
  const choicesFrom = response.choicesFrom;
  if (
    isRecord(choicesFrom) &&
    (choicesFrom.mode === "selected" || choicesFrom.mode === "unselected")
  ) {
    return null;
  }
  const bounds = rankingBounds(response);
  if (bounds.kind === "full") return asArray(response.options).length;
  return bounds.min === bounds.max ? bounds.max : null;
}

type JsonObject = Record<string, unknown>;

/** Inclusive continuous bounds from authored min/max, else unit registry, else none. */
export function continuousThresholdBounds(
  response: JsonObject,
): [number, number] | null {
  const authoredMin =
    typeof response.min === "number" && Number.isFinite(response.min)
      ? response.min
      : undefined;
  const authoredMax =
    typeof response.max === "number" && Number.isFinite(response.max)
      ? response.max
      : undefined;
  if (authoredMin !== undefined || authoredMax !== undefined) {
    return [
      authoredMin ?? Number.NEGATIVE_INFINITY,
      authoredMax ?? Number.POSITIVE_INFINITY,
    ];
  }
  const unit = response.unit;
  if (typeof unit !== "string" || !isContinuousUnitId(unit)) {
    return null;
  }
  const entry = getUnitRegistryEntry(unit);
  if (
    !entry ||
    typeof entry.rawMin !== "number" ||
    typeof entry.rawMax !== "number"
  ) {
    return null;
  }
  return [entry.rawMin, entry.rawMax];
}

export interface DraftValidationResult {
  status: "valid" | "invalid";
  issues: Array<QuestionnaireValidationIssue>;
}

export function isRecord(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function asArray(value: unknown): Array<unknown> {
  return Array.isArray(value) ? value : [];
}

// Matrix column caps match the heatmap slide contract, and are owned by
// `config/questionnaire-question-capabilities.json` (Python reads the same
// file). Column overflow is an error so launch cannot produce a grid the deck
// cannot render. Rows have no questionnaire cap: named-item rosters are study
// inputs, and the deck paginates them.
export const MAX_MATRIX_MEASURES = MATRIX_MAX_COLUMNS;
export const MAX_MATRIX_CATEGORIES = MATRIX_MAX_COLUMNS;

/**
 * Longest label the deck renders whole (`questionLabelMaxChars`,
 * `config/questionnaire-text-limits.json`, the slide schemas' `maxLength`; a
 * parity test holds the two equal). A label a model writes is held to it
 * (`author_label_too_long`, `models/questionnaire/label_cap.py`). A longer one
 * the user wrote is kept: the deck cuts it at the ceiling with an internal
 * `slide_text_cut` warning and the report is built (owner rule 2026-10-16:
 * layout never fails or warns the user), so the draft raises no issue for it.
 */
export { MAX_QUESTION_LABEL_LENGTH };

// The research-question bounds are owned by
// `config/questionnaire-question-capabilities.json` (Python reads the same
// file); they are exported from here too, where the count rule reads them.
export { DEFAULT_RESEARCH_QUESTION_FLOOR, RESEARCH_QUESTION_CEILING };

/**
 * Floor for an active ad instrument. Kept as a named export for source
 * compatibility; it equals the default now that there is no roster-derived
 * minimum. A parity test asserts it is never stricter than the default.
 */
export const AD_INSTRUMENT_MIN_RESEARCH_QUESTIONS =
  DEFAULT_RESEARCH_QUESTION_FLOOR;

/**
 * The minimum legal research-question count for a draft: always 1.
 *
 * The one owner: the backend runtime (`runtime.ts`) re-exports it. Product
 * decision (2026-08-15): no minimum beyond a single question. The draft
 * argument is kept so call sites stay source-compatible.
 */
export function resolveResearchQuestionFloor(_draft: unknown): number {
  return DEFAULT_RESEARCH_QUESTION_FLOOR;
}

/**
 * Raise an issue. Its severity is the rule catalog's user decision
 * (`issueSeverity`), never the raise site's: one severity source (T1b).
 */
export function addIssue(
  issues: Array<QuestionnaireValidationIssue>,
  path: string,
  code: string,
  message: string,
  params?: QuestionnaireValidationIssue["params"],
): void {
  issues.push({ path, code, message, severity: issueSeverity(code), ...(params ? { params } : {}) });
}

/**
 * The study objective's size rule (rule catalog S50): the decision and its
 * learn bullets share one STUDY_OBJECTIVE_MAX_CHARS budget, and there are at
 * most STUDY_OBJECTIVE_MAX_BULLETS bullets. Nothing cuts an objective to fit:
 * a model's `set_study_goals` over either is refused with these limits, and
 * any other objective over either blocks Review.
 */
export function studyObjectiveIssues(
  goals: unknown,
  path = "studyGoals",
): Array<QuestionnaireValidationIssue> {
  if (!isRecord(goals)) return [];
  const issues: Array<QuestionnaireValidationIssue> = [];
  const decision = typeof goals.decision === "string" ? goals.decision : "";
  const learn = asArray(goals.learn).filter(
    (bullet): bullet is string => typeof bullet === "string",
  );
  const length = learn.reduce(
    (total, bullet) => total + bullet.length,
    decision.length,
  );
  if (length > STUDY_OBJECTIVE_MAX_CHARS) {
    addIssue(
      issues,
      path,
      "study_objective_too_long",
      repairLine("study_objective_too_long", { length, limit: STUDY_OBJECTIVE_MAX_CHARS }),
      { length, limit: STUDY_OBJECTIVE_MAX_CHARS },
    );
  }
  if (learn.length > STUDY_OBJECTIVE_MAX_BULLETS) {
    addIssue(
      issues,
      `${path}.learn`,
      "study_objective_too_many_points",
      repairLine("study_objective_too_many_points", { count: learn.length, limit: STUDY_OBJECTIVE_MAX_BULLETS }),
      { count: learn.length, limit: STUDY_OBJECTIVE_MAX_BULLETS },
    );
  }
  return issues;
}

/**
 * Markup in objective text a model wrote (`study_objective_markup`): a
 * tag-like `<…>` (`<` before a letter or `/`, so "margin < 5% > target" is
 * prose) or a backtick (inline code or a fence). Syntax only, never meaning.
 * The user's own text is stored as typed and shown as plain text everywhere
 * (owner 2026-10-22); only a model writer is refused.
 */
const OBJECTIVE_MARKUP_PATTERNS: ReadonlyArray<RegExp> = [/<\/?[a-zA-Z][^>]*>/, /`/];

// A model-only refusal the applier sends back for repair: never a draft issue
// a reader shows, so it carries no display severity (the entry has no user
// decision; a user's objective is stored as typed).
export const objectiveMarkupIssues = ruleImpl('study_objective_markup', (
  goals: unknown,
  path = "studyGoals",
): Array<{ path: string; code: "study_objective_markup"; message: string; params?: undefined }> => {
  if (!isRecord(goals)) return [];
  const texts: Array<[string, unknown]> = [
    [`${path}.decision`, goals.decision],
    ...asArray(goals.learn).map((point, index): [string, unknown] => [`${path}.learn[${index}]`, point]),
  ];
  return texts.flatMap(([at, text]) =>
    typeof text === "string" && OBJECTIVE_MARKUP_PATTERNS.some((pattern) => pattern.test(text))
      ? [{ path: at, code: "study_objective_markup" as const, message: repairLine("study_objective_markup") }]
      : [],
  );
});

/**
 * The design brief's size rules (owner 2026-10-22): each text a writer gives it
 * is at most its limit in `config/questionnaire-text-limits.json`, counted in
 * code points, with its own code (the limit is the code's fact). Nothing cuts
 * a brief to fit: a model writer's schema states the same limits and its
 * stage leaves a text still over after its repair out, and a stored brief
 * over one blocks Review with the limit.
 */
const DESIGN_BRIEF_TEXT_RULES: ReadonlyArray<[string, RuleCode, number]> = [
  ["method", "design_brief_method_too_long", DESIGN_BRIEF_METHOD_MAX_CHARS],
  ["primaryMetric", "design_brief_line_too_long", DESIGN_BRIEF_LINE_MAX_CHARS],
  ["namedAction", "design_brief_line_too_long", DESIGN_BRIEF_LINE_MAX_CHARS],
  ["authoredObjective.title", "study_name_too_long", STUDY_NAME_MAX_CHARS],
  // The user's own sentences about the situation, joined by code: the
  // objective's budget.
  // (read from `./text-limits`: this table is built at load, before the
  // `./index` cycle resolves).
  ["situationalContext", "situational_context_too_long", SITUATION_MAX_CHARS],
];

export function designBriefIssues(
  brief: unknown,
  path = "designBrief",
): Array<QuestionnaireValidationIssue> {
  if (!isRecord(brief)) return [];
  const issues: Array<QuestionnaireValidationIssue> = [];
  for (const [field, code, limit] of DESIGN_BRIEF_TEXT_RULES) {
    const text = field
      .split(".")
      .reduce<unknown>((node, key) => (isRecord(node) ? node[key] : undefined), brief);
    if (typeof text !== "string") continue;
    const length = textLength(text);
    if (length > limit) {
      addIssue(issues, `${path}.${field}`, code, repairLine(code, { length }), { length, limit });
    }
  }
  return issues;
}

export function hasMatrixRowsPlaceholder(question: JsonObject): boolean {
  return asArray(question.placeholderSubstitutions).some(
    (entry) => isRecord(entry) && entry.site === "matrix_rows",
  );
}

export function responseOf(question: JsonObject): JsonObject {
  return isRecord(question.response) ? question.response : {};
}

/**
 * The field of a select or categorical-grid response that shuffles an answer
 * list stated `ordered` (an ordinal sequence is shown in its order, rule
 * catalog `plan_ordered_randomized`), else null. The one predicate: the
 * validator holds it on every stored response, and the applier refuses a
 * written response by it early, at the operation's path.
 */
export function orderedShuffleField(response: unknown): "randomizeOptions" | "randomizeCategories" | null {
  if (!isRecord(response) || response.ordered !== true) return null;
  const field = response.kind === "matrix_categorical" ? "randomizeCategories" : "randomizeOptions";
  return response[field] === true ? field : null;
}
ruleImpl("plan_ordered_randomized", orderedShuffleField);

/**
 * The content a tested asset still owes, by the asset-family registry: a
 * description for the families that need one (`DESCRIPTION_REQUIRED_FAMILIES`)
 * and a message's text (`requiredFields`), each only when the block is
 * `required` and no content is supplied. The one predicate of what a
 * required block's asset owes: the validator raises `message_text_required`
 * from it, readiness's asset-content check (`ordinalAssetContentIssues`)
 * `concept_description_required`, and the chat's pre-Build ask reads it rather
 * than its own copy.
 */
export function missingAssetContent(
  asset: JsonObject,
  required: boolean,
): Array<"concept_description_required" | "message_text_required"> {
  if (!required || !isAssetFamily(asset.type) || assetContentSupplied(asset)) return [];
  const owed: Array<"concept_description_required" | "message_text_required"> = [];
  if (DESCRIPTION_REQUIRED_FAMILIES.has(asset.type)) owed.push("concept_description_required");
  if (ASSET_FAMILY_FACTS[asset.type].requiredFields.includes("messageText")) owed.push("message_text_required");
  return owed;
}

/** Whether a choice offers enough answer options or grid categories. */
function choiceMemberCount(count: number): Array<RuleFailure> {
  return countWithinBounds({ min: MIN_CHOICE_MEMBERS }, { count });
}

function validateChoiceResponse(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
  assetsById: Map<string, JsonObject>,
  wording: PriceWording,
): void {
  const options = asArray(response.options);
  const [tooFewOptions] = choiceMemberCount(options.length);
  if (tooFewOptions?.reason === "below_min") {
    addIssue(
      issues,
      `${path}.options`,
      "choice_options_too_few",
      repairLine("choice_options_too_few"),
    );
  }

  // Duplicate option labels confuse respondents. A blank label is refused
  // where it is written (the applier's `option_label_blank`), its one owner.
  const seenOptionLabels = new Map<string, number>();
  options.forEach((option, optionIndex) => {
    const label =
      isRecord(option) && typeof option.label === "string"
        ? normalText(option.label)
        : "";
    if (!label) return;
    const rendered = isRecord(option)
      ? exactChoiceAssetRefAnswerLabel(option, [...assetsById.values()], wording)
      : null;
    const key = normalText(rendered ?? label).toLowerCase();
    const firstIndex = seenOptionLabels.get(key);
    if (firstIndex !== undefined) {
      addIssue(
        issues,
        `${path}.options[${optionIndex}].label`,
        "option_label_duplicate",
        repairLine("option_label_duplicate", { firstIndex: String(firstIndex) }),
      );
    } else {
      seenOptionLabels.set(key, optionIndex);
    }
  });
}

function validateRuntimeRatingUnit(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
): void {
  if (
    typeof response.unit === "string" &&
    isRatingUnitId(response.unit) &&
    scaleExecutable({ scales: RATING_SCALES }, { unit: response.unit }).length > 0
  ) {
    addIssue(
      issues,
      `${path}.unit`,
      "rating_scale_not_executable",
      repairLine("rating_scale_not_executable", { supportedUnits: SUPPORTED_RATING_UNIT_IDS }),
    );
  }
}

// Fully-labeled scales: pointLabels is optional, but when present it must be a
// well-formed label-per-point array for the unit. Length mismatches are errors
// (the compiled scale would be ambiguous); blank labels are warnings because the
// compiler degrades them to the numeric value rather than dropping the point.
export function validateRatingPointLabels(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
): void {
  const pointLabels = response.pointLabels;
  // null is treated as absent (LLMs emit null for optional fields) — keep in
  // sync with the python and backend-runtime mirrors of this function.
  if (pointLabels === undefined || pointLabels === null) {
    return;
  }
  if (
    !Array.isArray(pointLabels) ||
    pointLabels.some((label) => typeof label !== "string")
  ) {
    addIssue(
      issues,
      `${path}.pointLabels`,
      "point_labels_invalid",
      repairLine("point_labels_invalid"),
    );
    return;
  }
  const unit = typeof response.unit === "string" ? response.unit : "";
  if (unit === "rating_1_7") {
    addIssue(
      issues,
      `${path}.pointLabels`,
      "point_labels_legacy_scale",
      repairLine("point_labels_legacy_scale"),
    );
    return;
  }
  // A scale whose points take no labels (binary_yes_no: the compiler
  // hardcodes Yes/No and the builder drops its labels) is `pointLabels: false`
  // in the scale registry, so accepting labels here would validate a draft
  // the rest of the pipeline silently rewrites.
  const [scaleFailure] = scaleExecutable(
    { scales: RATING_SCALES },
    { unit, pointLabelCount: pointLabels.length },
  );
  if (
    scaleFailure?.reason === "unsupported_scale" ||
    scaleFailure?.reason === "point_labels_not_taken"
  ) {
    addIssue(
      issues,
      `${path}.pointLabels`,
      "point_labels_unsupported_unit",
      repairLine("point_labels_unsupported_unit", { englishList: String(englishList(POINT_LABEL_RATING_UNIT_IDS)) }),
    );
    return;
  }
  if (scaleFailure?.reason === "point_label_count") {
    addIssue(
      issues,
      `${path}.pointLabels`,
      "point_labels_length_mismatch",
      repairLine("point_labels_length_mismatch", { points: String(scaleFailure.points), unit: String(unit), pointLabelsCount: String(pointLabels.length) }),
    );
    return;
  }
  // One warning per scale, however many of its points are blank: its line
  // speaks of the scale, so one per point repeated it on the card.
  if (
    pointLabels.some(
      (label) => typeof label === "string" && label.trim().length === 0,
    )
  ) {
    addIssue(
      issues,
      `${path}.pointLabels`,
      "point_label_empty",
      repairLine("point_label_empty"),
    );
  }
}

export function validateNotApplicableLabel(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
): void {
  const label = response.notApplicableLabel;
  if (label === undefined || label === null) return;
  if (typeof label !== "string" || label.trim().length === 0) {
    addIssue(
      issues,
      `${path}.notApplicableLabel`,
      "not_applicable_label_invalid",
      repairLine("not_applicable_label_invalid"),
    );
    return;
  }
  if (response.includeNotApplicable !== true) {
    addIssue(
      issues,
      `${path}.notApplicableLabel`,
      "not_applicable_label_without_option",
      repairLine("not_applicable_label_without_option"),
    );
  }
}

type MatrixAssetContext = {
  declaredAssetTypes: Set<string>;
  testedAssets: Array<JsonObject>;
};

function validateMatrixRows(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  path: string,
  matrixAssetContext?: MatrixAssetContext,
): void {
  const rows = isRecord(response.rows) ? response.rows : {};
  const source = rows.source;
  // Any other source is the closed shape's (`matrixRows`) to refuse.
  if (source !== "fixed" && source !== "tested_assets") return;

  const [tooFewRows] = countWithinBounds(
    { min: MATRIX_ROWS_FLOOR },
    { count: asArray(rows.rows).length },
  );
  if (source === "fixed" && tooFewRows?.reason === "below_min") {
    addIssue(
      issues,
      `${path}.rows.rows`,
      "matrix_rows_too_few",
      repairLine("matrix_rows_too_few", { minValue: String(tooFewRows.min) }),
    );
  }

  // A blank row label is refused where it is written (the applier's
  // `row_label_blank`), its one owner.

  if (source === "tested_assets" && typeof rows.assetType !== "string") {
    addIssue(
      issues,
      `${path}.rows.assetType`,
      "matrix_asset_type_required",
      repairLine("matrix_asset_type_required"),
    );
    return;
  }

  if (
    source === "tested_assets" &&
    typeof rows.assetType === "string" &&
    matrixAssetContext
  ) {
    const assetType = rows.assetType;
    if (!matrixAssetContext.declaredAssetTypes.has(assetType)) {
      addIssue(
        issues,
        `${path}.rows.assetType`,
        "matrix_asset_type_undeclared",
        repairLine("matrix_asset_type_undeclared", { assetType: String(assetType), value: String([
          ...matrixAssetContext.declaredAssetTypes,
        ].join(", ")) }),
      );
    }

    const matchingAssets = matrixAssetContext.testedAssets.filter(
      (asset) => isRecord(asset) && asset.type === assetType,
    );
    if (matchingAssets.length === 0) {
      const availableTypes = [
        ...new Set(
          matrixAssetContext.testedAssets
            .filter(
              (asset): asset is JsonObject =>
                isRecord(asset) && typeof asset.type === "string",
            )
            .map((asset) => asset.type as string),
        ),
      ];
      addIssue(
        issues,
        `${path}.rows.assetType`,
        "matrix_rows_no_matching_assets",
        repairLine("matrix_rows_no_matching_assets", { assetType: String(assetType), availableTypes: String(availableTypes.length > 0 ? availableTypes.join(", ") : "none") }),
      );
    }
  }
}

export function collectStableKey(
  issues: Array<QuestionnaireValidationIssue>,
  seenStableKeys: Map<string, string>,
  stableKey: unknown,
  path: string,
): void {
  if (typeof stableKey !== "string") {
    return;
  }

  // One namespace, compared case aside as the key rule compares keys.
  const identity = keyIdentity(stableKey);
  const firstPath = seenStableKeys.get(identity);
  if (firstPath) {
    addIssue(
      issues,
      path,
      "duplicate_stable_key",
      repairLine("duplicate_stable_key", { firstPath: String(firstPath) }),
    );
    return;
  }

  seenStableKeys.set(identity, path);
}

function validateQuestion(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
  wording: PriceWording,
  matrixAssetContext?: MatrixAssetContext,
  assetsById: Map<string, JsonObject> = new Map(),
): void {
  const type = question.type;
  const response = responseOf(question);
  const responsePath = `${questionPath}.response`;

  if (hasMatrixRowsPlaceholder(question)) {
    addIssue(
      issues,
      questionPath,
      "matrix_rows_placeholder",
      repairLine("matrix_rows_placeholder"),
    );
  }

  if (valueInList({ values: QUESTION_TYPE_IDS }, { value: type }).length > 0) {
    addIssue(
      issues,
      `${questionPath}.type`,
      "unsupported_question_type",
      repairLine("unsupported_question_type", { type: String(String(type)) }),
    );
    return;
  }

  if (response.kind !== type) {
    addIssue(
      issues,
      `${responsePath}.kind`,
      "response_kind_mismatch",
      repairLine("response_kind_mismatch"),
    );
  }

  // Copy quality: advice by the catalog's decision, never a block.
  if (
    typeof question.title === "string" &&
    respondentTextGluesQuestionStems(question.title)
  ) {
    addIssue(
      issues,
      `${questionPath}.title`,
      "question_stem_glued",
      repairLine("question_stem_glued", { field: "title", form: "stem" }),
    );
  }
  if (
    typeof question.description === "string" &&
    respondentTextGluesQuestionStems(question.description)
  ) {
    addIssue(
      issues,
      `${questionPath}.description`,
      "question_stem_glued",
      repairLine("question_stem_glued", { field: "description", form: "instruction" }),
    );
  }

  if ((SELECT_QUESTION_TYPES as ReadonlySet<unknown>).has(type)) {
    validateChoiceResponse(issues, response, responsePath, assetsById, wording);
  }
  const shuffledOrder = orderedShuffleField(response);
  if (shuffledOrder) {
    addIssue(
      issues,
      `${responsePath}.${shuffledOrder}`,
      "plan_ordered_randomized",
      worded("plan_ordered_randomized", requiredRefusalLine("plan_ordered_randomized")),
    );
  }

  if (type === "ranking") {
    const options = asArray(response.options);
    const [tooFewOptions] = choiceMemberCount(options.length);
    if (tooFewOptions?.reason === "below_min") {
      addIssue(
        issues,
        `${responsePath}.options`,
        "ranking_options_too_few",
        repairLine("ranking_options_too_few", { minValue: String(tooFewOptions.min) }),
      );
    }
  }

  if (type === "rating") {
    validateRuntimeRatingUnit(issues, response, responsePath);
    validateRatingPointLabels(issues, response, responsePath);
    validateNotApplicableLabel(issues, response, responsePath);
  }

  // A whole-number unit (a count) has no decimal places.
  if (
    type === "continuous" &&
    response.precision != null &&
    (!Number.isInteger(response.precision) ||
      Number(response.precision) < 0 ||
      (isWholeNumberUnit(response.unit) && response.precision !== 0))
  ) {
    addIssue(
      issues,
      `${responsePath}.precision`,
      "continuous_precision_invalid",
      repairLine("continuous_precision_invalid"),
    );
  }

  if (type === "continuous" && response.unit === "duration") {
    if (typeof response.durationUnit !== "string") {
      addIssue(
        issues,
        `${responsePath}.durationUnit`,
        "duration_unit_required",
        repairLine("duration_unit_required"),
      );
    } else if (
      !DURATION_EXPOSED_UNITS.includes(response.durationUnit as never)
    ) {
      addIssue(
        issues,
        `${responsePath}.durationUnit`,
        "unsupported_duration_unit",
        repairLine("unsupported_duration_unit", { englishList: String(englishList(DURATION_EXPOSED_UNITS)) }),
      );
    }
  }

  if (type === "matrix_rating") {
    validateMatrixRows(issues, response, responsePath, matrixAssetContext);
    const measures = asArray(response.measures);
    if (measures.length < 1) {
      addIssue(
        issues,
        `${responsePath}.measures`,
        "matrix_measures_too_few",
        repairLine("matrix_measures_too_few"),
      );
    }
    const [tooManyMeasures] = countWithinBounds(
      { max: MAX_MATRIX_MEASURES },
      { count: measures.length },
    );
    if (tooManyMeasures?.reason === "above_max") {
      addIssue(
        issues,
        `${responsePath}.measures`,
        "matrix_measures_too_many",
        repairLine("matrix_measures_too_many", { maxValue: String(tooManyMeasures.max), measuresCount: String(measures.length) }),
      );
    }
    // All measures in one matrix should share a single rating scale — mixing
    // e.g. 0-10 with 1-5 makes the columns incomparable, and stamping and
    // the report both read one scale per grid, so it blocks.
    const measureUnits = new Set<string>();
    measures.forEach((measure) => {
      if (isRecord(measure) && typeof measure.unit === "string") {
        measureUnits.add(measure.unit);
      }
    });
    if (measureUnits.size > 1) {
      addIssue(
        issues,
        `${responsePath}.measures`,
        "matrix_measures_mixed_units",
        repairLine("matrix_measures_mixed_units"),
      );
    }

    measures.forEach((measure, measureIndex) => {
      // A measure that is not an object is the closed shape's to refuse.
      if (!isRecord(measure)) return;
      validateRuntimeRatingUnit(
        issues,
        measure,
        `${responsePath}.measures[${measureIndex}]`,
      );
      validateRatingPointLabels(
        issues,
        measure,
        `${responsePath}.measures[${measureIndex}]`,
      );
      validateNotApplicableLabel(
        issues,
        measure,
        `${responsePath}.measures[${measureIndex}]`,
      );

      // A measure with no label renders as a phantom column. A blank one is
      // refused where it is written (the applier's `scale_label_blank`).
      if (typeof measure.label !== "string") {
        addIssue(
          issues,
          `${responsePath}.measures[${measureIndex}].label`,
          "matrix_measure_label_missing",
          repairLine("matrix_measure_label_missing"),
        );
      }
    });
  }

  if (type === "matrix_categorical") {
    validateMatrixRows(issues, response, responsePath, matrixAssetContext);
    const categories = asArray(response.categories);
    const [tooFewCategories] = choiceMemberCount(categories.length);
    if (tooFewCategories?.reason === "below_min") {
      addIssue(
        issues,
        `${responsePath}.categories`,
        "matrix_categories_too_few",
        repairLine("matrix_categories_too_few", { minValue: String(tooFewCategories.min) }),
      );
    }
    const [tooManyCategories] = countWithinBounds(
      { max: MAX_MATRIX_CATEGORIES },
      { count: categories.length },
    );
    if (tooManyCategories?.reason === "above_max") {
      addIssue(
        issues,
        `${responsePath}.categories`,
        "matrix_categories_too_many",
        repairLine("matrix_categories_too_many", { maxValue: String(tooManyCategories.max), categoriesCount: String(categories.length) }),
      );
    }
  }
}

function validateRecipeModuleQuestionType(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
  payload: JsonObject,
): void {
  const mismatch = recipeModuleTypeMismatchOf(payload, question);
  if (!mismatch) {
    return;
  }
  addIssue(
    issues,
    `${questionPath}.type`,
    "question_recipe_module_type_mismatch",
    repairLine("question_recipe_module_type_mismatch", {
      actual: mismatch.actual,
      moduleId: mismatch.moduleId,
      expected: mismatch.expected,
    }),
  );
}

export function validateAssetBindings(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
  declaredAssetTypes: Set<string>,
): void {
  if (!isRecord(question.assetBinding)) {
    return;
  }

  asArray(question.assetBinding.assetTypes).forEach(
    (assetType, assetTypeIndex) => {
      if (typeof assetType !== "string" || !declaredAssetTypes.has(assetType)) {
        addIssue(
          issues,
          `${questionPath}.assetBinding.assetTypes[${assetTypeIndex}]`,
          "asset_binding_type_missing",
          repairLine("asset_binding_type_missing"),
        );
      }
    },
  );
}

export function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function hasGcsImageReference(mediaRef: JsonObject): boolean {
  if (hasText(mediaRef.gcsUri) && mediaRef.gcsUri.trim().startsWith("gs://")) {
    return true;
  }
  if (hasText(mediaRef.bucket) && hasText(mediaRef.objectKey)) {
    return true;
  }
  const publicUrl = hasText(mediaRef.publicUrl)
    ? mediaRef.publicUrl
    : hasText(mediaRef.url)
      ? mediaRef.url
      : hasText(mediaRef.filePath)
        ? mediaRef.filePath
        : "";
  return /^https:\/\/storage\.googleapis\.com\/[^/]+\/.+/i.test(
    publicUrl.trim(),
  );
}

function validateTestedAssets(
  issues: Array<QuestionnaireValidationIssue>,
  testedAssets: JsonObject,
  seenStableKeys: Map<string, string>,
): {
  assetIds: Set<string>;
  assetTypes: Set<string>;
  assetsById: Map<string, JsonObject>;
} {
  const assetTypes = new Set(
    asArray(testedAssets.assetTypes).filter(
      (assetType): assetType is string => typeof assetType === "string",
    ),
  );
  const assetIds = new Set<string>();
  const assetsById = new Map<string, JsonObject>();
  const stableKeys = new Set<string>();
  const required = testedAssets.required === true;

  const sharedContext = testedAssets.sharedContext;
  if (sharedContext !== undefined) {
    const contextPath = "testedAssets.sharedContext";
    if (!isRecord(sharedContext)) {
      addIssue(
        issues,
        contextPath,
        "asset_shared_context_invalid",
        worded("asset_shared_context_invalid", "Shared asset context must carry literal source provenance."),
      );
    } else {
      const provenance = isRecord(sharedContext.provenance)
        ? sharedContext.provenance
        : {};
      const segments = asArray(provenance.segments);
      const completeSegments = segments.filter(
        (segment) =>
          isRecord(segment) &&
          hasText(segment.sourceId) &&
          typeof segment.text === "string" &&
          segment.text.trim().length > 0,
      ) as Array<JsonObject>;
      const assembled = completeSegments
        .map((segment) => segment.text as string)
        .join("\n");
      if (
        sharedContext.schemaVersion !== "tested-asset-shared-context.v1" ||
        !hasText(sharedContext.text) ||
        provenance.kind !== "literal_source_segments" ||
        segments.length === 0 ||
        completeSegments.length !== segments.length ||
        assembled !== sharedContext.text
      ) {
        addIssue(
          issues,
          contextPath,
          "asset_shared_context_invalid",
          worded("asset_shared_context_invalid", "Shared asset context must equal its ordered, non-empty literal source segments."),
        );
      } else if (!isWithinAssetDescriptionWordLimit(sharedContext.text)) {
        addIssue(
          issues,
          `${contextPath}.text`,
          "asset_shared_context_too_long",
          repairLine("asset_shared_context_too_long", { toLocaleString: String(MAX_ASSET_DESCRIPTION_WORDS.toLocaleString()) }),
        );
      } else if (asArray(testedAssets.assets).length === 0) {
        addIssue(
          issues,
          contextPath,
          "asset_shared_context_without_assets",
          repairLine("asset_shared_context_without_assets"),
        );
      }
    }
  }

  asArray(testedAssets.assets).forEach((asset, index) => {
    if (!isRecord(asset)) {
      addIssue(
        issues,
        `testedAssets.assets[${index}]`,
        "asset_invalid",
        repairLine("asset_invalid"),
      );
      return;
    }

    const path = `testedAssets.assets[${index}]`;
    ["id", "type", "stableKey", "name"].forEach((field) => {
      if (!hasText(asset[field])) {
        addIssue(
          issues,
          `${path}.${field}`,
          "asset_required_field_missing",
          worded("asset_required_field_missing", `Asset requires ${field}.`),
        );
      }
    });

    if (hasText(asset.id)) {
      assetIds.add(asset.id);
      assetsById.set(asset.id, asset);
    }

    if (hasText(asset.stableKey)) {
      collectStableKey(
        issues,
        seenStableKeys,
        asset.stableKey,
        `${path}.stableKey`,
      );
      if (stableKeys.has(asset.stableKey)) {
        addIssue(
          issues,
          `${path}.stableKey`,
          "duplicate_asset_stable_key",
          worded("duplicate_asset_stable_key", "Asset stable keys must be unique within a draft."),
        );
      }
      stableKeys.add(asset.stableKey);
    }

    // A description the registry requires (QA4 AD N4) is raised by the one
    // asset-content check, readiness's `ordinalAssetContentIssues`.
    const owedContent = missingAssetContent(asset, required);

    if (
      hasText(asset.description) &&
      !isWithinAssetDescriptionWordLimit(asset.description)
    ) {
      addIssue(
        issues,
        `${path}.description`,
        "asset_description_too_long",
        repairLine("asset_description_too_long", { toLocaleString: String(MAX_ASSET_DESCRIPTION_WORDS.toLocaleString()) }),
      );
    }

    // The family's other required fields (`requiredFields`: a message's
    // text) must be non-empty (warning — surfaces an incomplete asset without
    // blocking existing drafts).
    if (owedContent.includes("message_text_required")) {
      addIssue(
        issues,
        `${path}.messageText`,
        "message_text_required",
        worded("message_text_required", "Message assets require the claim text (messageText)."),
      );
    }


    asArray(asset.media).forEach((mediaRef, mediaIndex) => {
      if (!isRecord(mediaRef)) {
        addIssue(
          issues,
          `${path}.media[${mediaIndex}]`,
          "asset_media_invalid",
          repairLine("asset_media_invalid"),
        );
        return;
      }
      if (mediaRef.kind === "image" && !hasGcsImageReference(mediaRef)) {
        addIssue(
          issues,
          `${path}.media[${mediaIndex}]`,
          "asset_image_gcs_reference_required",
          repairLine("asset_image_gcs_reference_required"),
        );
      }
      const unreadableVideo = nonKeyframeableVideoIssue(mediaRef);
      if (unreadableVideo) {
        addIssue(
          issues,
          `${path}.media[${mediaIndex}]`,
          "asset_video_not_keyframeable",
          worded("asset_video_not_keyframeable", unreadableVideo),
        );
      }
    });
  });

  return { assetIds, assetTypes, assetsById };
}

/**
 * Why twins cannot watch this video, or null. Twins need ffmpeg keyframes,
 * which a YouTube link or a player-page host (Vimeo, Drive, Dropbox) cannot
 * give: the one rule readiness, compile and launch read (QA4 A2 #10). An
 * uploaded file (`gs://`, `data:`) or no link at all is not this rule's.
 */
function nonKeyframeableVideoIssue(mediaRef: JsonObject): string | null {
  if (mediaRef.kind !== "video") return null;
  const url = String(mediaRef.publicUrl || mediaRef.url || "").trim();
  if (mediaRef.mimeType === "video/youtube") {
    return nonKeyframeableVideoMessage(url) ?? YOUTUBE_NON_KEYFRAMEABLE_MESSAGE;
  }
  if (!url || url.startsWith("data:") || url.startsWith("gs://")) return null;
  return nonKeyframeableVideoMessage(url);
}

export function validateAssetRefs(
  issues: Array<QuestionnaireValidationIssue>,
  owner: JsonObject,
  ownerPath: string,
  assetsById: Map<string, JsonObject>,
): void {
  asArray(owner.assetRefs).forEach((assetRef, assetRefIndex) => {
    const path = `${ownerPath}.assetRefs[${assetRefIndex}]`;
    if (!isRecord(assetRef)) {
      addIssue(
        issues,
        path,
        "asset_ref_invalid",
        repairLine("asset_ref_invalid"),
      );
      return;
    }

    const assetId = hasText(assetRef.assetId) ? assetRef.assetId : "";
    const asset = assetId ? assetsById.get(assetId) : undefined;
    if (!assetId || !asset) {
      addIssue(
        issues,
        `${path}.assetId`,
        "asset_ref_missing",
        worded("asset_ref_missing", "Question asset references must point to an existing tested asset."),
      );
      return;
    }

    if (
      hasText(assetRef.assetStableKey) &&
      assetRef.assetStableKey !== asset.stableKey
    ) {
      addIssue(
        issues,
        `${path}.assetStableKey`,
        "asset_ref_stable_key_mismatch",
        worded("asset_ref_stable_key_mismatch", "Question asset reference stable key must match the tested asset."),
      );
    }
    if (hasText(assetRef.assetType) && assetRef.assetType !== asset.type) {
      addIssue(
        issues,
        `${path}.assetType`,
        "asset_ref_type_mismatch",
        worded("asset_ref_type_mismatch", "Question asset reference type must match the tested asset."),
      );
    }
  });
}

function validateChoiceOptionAssetRefs(
  issues: Array<QuestionnaireValidationIssue>,
  response: JsonObject,
  responsePath: string,
  assetsById: Map<string, JsonObject>,
): void {
  asArray(response.options).forEach((option, optionIndex) => {
    if (isRecord(option)) {
      validateAssetRefs(
        issues,
        option,
        `${responsePath}.options[${optionIndex}]`,
        assetsById,
      );
    }
  });
  asArray(response.categories).forEach((category, categoryIndex) => {
    if (isRecord(category)) {
      validateAssetRefs(
        issues,
        category,
        `${responsePath}.categories[${categoryIndex}]`,
        assetsById,
      );
    }
  });
  // A fixed grid row's refs are the same rule as any other asset ref (the
  // one owner; the accepted-artifact relations kept a stricter copy).
  const rows = isRecord(response.rows) ? response.rows : null;
  if (rows?.source === "fixed") {
    asArray(rows.rows).forEach((row, rowIndex) => {
      if (isRecord(row)) {
        validateAssetRefs(issues, row, `${responsePath}.rows.rows[${rowIndex}]`, assetsById);
      }
    });
  }
}

export function collectCompositeMetricIds(draft: JsonObject): Set<string> {
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const compositeMetricIds = new Set<string>();

  asArray(metricPlan.compositeMetrics).forEach((compositeMetric) => {
    if (!isRecord(compositeMetric)) {
      return;
    }

    asArray(compositeMetric.components).forEach((component) => {
      if (isRecord(component) && typeof component.metricId === "string") {
        compositeMetricIds.add(component.metricId);
      }
    });
  });

  return compositeMetricIds;
}

export function collectMetricIds(draft: JsonObject): Set<string> {
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const metricIds = new Set<string>();
  asArray(metricPlan.metrics).forEach((metric) => {
    if (isRecord(metric) && typeof metric.id === "string") {
      metricIds.add(metric.id);
    }
  });
  return metricIds;
}

export function validateCompositeReferences(
  issues: Array<QuestionnaireValidationIssue>,
  draft: JsonObject,
): void {
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const composites = asArray(metricPlan.compositeMetrics);
  if (composites.length === 0) {
    return;
  }

  const metricIds = collectMetricIds(draft);

  // T4 creates these declared classifier outputs at scoring time. Keep this
  // composite-only: they are not ordinary question or slide metric references.
  for (const analysis of asArray(metricPlan.qualitativeAnalyses)) {
    if (!isRecord(analysis) || typeof analysis.stableKey !== "string") {
      continue;
    }
    const classifier = analysis.classifier;
    if (
      isRecord(classifier) &&
      typeof classifier.metricKind === "string" &&
      classifier.metricKind.length > 0
    ) {
      metricIds.add(
        openEndClassifierMetricId(analysis.stableKey, classifier.metricKind),
      );
    }
  }

  composites.forEach((composite, compositeIndex) => {
    if (!isRecord(composite)) {
      return;
    }
    const compositePath = `metricPlan.compositeMetrics[${compositeIndex}]`;
    const components = asArray(composite.components);
    let liveComponents = 0;

    components.forEach((component, componentIndex) => {
      if (!isRecord(component) || typeof component.metricId !== "string") {
        return;
      }
      if (metricIds.has(component.metricId)) {
        liveComponents += 1;
        return;
      }
      addIssue(
        issues,
        `${compositePath}.components[${componentIndex}].metricId`,
        "composite_metric_ref_missing",
        repairLine("composite_metric_ref_missing"),
      );
    });

    if (components.length > 0 && liveComponents === 0) {
      addIssue(
        issues,
        compositePath,
        "composite_has_no_live_components",
        repairLine("composite_has_no_live_components"),
      );
    }
  });
}

export function validateCompositeBounds(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
  compositeMetricIds: Set<string>,
): void {
  const response = responseOf(question);
  const unit = typeof response.unit === "string" ? response.unit : undefined;
  if (!unit || !unitRequiresExplicitBoundsForComposite(unit)) {
    return;
  }

  const participatesInComposite = asArray(question.metricRefs).some(
    (metricRef) =>
      typeof metricRef === "string" && compositeMetricIds.has(metricRef),
  );
  if (!participatesInComposite) {
    return;
  }

  if (typeof response.min !== "number" || typeof response.max !== "number") {
    addIssue(
      issues,
      `${questionPath}.response`,
      "composite_bounds_required",
      repairLine("composite_bounds_required"),
    );
  }
}

export function validateOpenEndCoverage(
  issues: Array<QuestionnaireValidationIssue>,
  draft: JsonObject,
  question: JsonObject,
  questionPath: string,
): void {
  if (question.type !== "open_end") {
    return;
  }

  const questionId = typeof question.id === "string" ? question.id : "";
  const metricPlan = isRecord(draft.metricPlan) ? draft.metricPlan : {};
  const qualitativeAnalyses = asArray(metricPlan.qualitativeAnalyses);
  const hasQualAnalysis = qualitativeAnalyses.some(
    (analysis) => isRecord(analysis) && analysis.questionId === questionId,
  );
  if (!hasQualAnalysis) {
    addIssue(
      issues,
      `${questionPath}.metricRefs`,
      "open_end_qual_missing",
      repairLine("open_end_qual_missing"),
    );
  }
  // The slide builder gives a Survey-Data-only question no slide.
  if (
    isSurveyDataOnlyQuestion(
      question as Parameters<typeof isSurveyDataOnlyQuestion>[0],
    )
  ) {
    return;
  }

  const slidePlan = isRecord(draft.slidePlan) ? draft.slidePlan : {};
  const ordering = asArray(slidePlan.ordering);
  const openEndSlideTypes = new Set(
    ordering
      .filter(
        (slide) =>
          isRecord(slide) && asArray(slide.questionIds).includes(questionId),
      )
      .map((slide) => (isRecord(slide) ? slide.slideType : undefined)),
  );

  const frame = isRecord(question.planAttribution)
    ? question.planAttribution.openEndAnalysisFrame
    : undefined;
  const expectedSlideTypes =
    frame === "factual"
      ? ["qual_descriptive_themes"]
      : ["qual_positive_themes", "qual_negative_themes"];

  const actualFrameSlideTypes = new Set<string>(
    [...openEndSlideTypes].filter(isQualitativeSlideType),
  );
  if (
    actualFrameSlideTypes.size !== expectedSlideTypes.length ||
    !expectedSlideTypes.every((slideType) => actualFrameSlideTypes.has(slideType))
  ) {
    addIssue(
      issues,
      `${questionPath}.slideRefs`,
      "open_end_slides_missing",
      worded("open_end_slides_missing", frame === "factual"
        ? "Factual open-end questions require only a descriptive qualitative slide."
        : "Evaluative open-end questions require only positive and negative qual slides."),
    );
  }
}

export function collectMatrixVisibilityRowValues(
  response: JsonObject,
  assetsById: Map<string, JsonObject>,
): Set<string> {
  const rows = isRecord(response.rows) ? response.rows : {};
  if (rows.source === "fixed") {
    return new Set(
      asArray(rows.rows)
        .filter(isRecord)
        .map((row) => row.value)
        .filter(
          (value): value is string =>
            typeof value === "string" && value.length > 0,
        ),
    );
  }
  if (rows.source === "tested_assets" && typeof rows.assetType === "string") {
    const assetType = rows.assetType;
    return new Set(
      [...assetsById.values()]
        .filter(
          (asset) => asset.type === assetType && typeof asset.id === "string",
        )
        .map((asset) => asset.id as string),
    );
  }
  return new Set();
}

function validateMatrixRatingVisibilityRule(
  issues: Array<QuestionnaireValidationIssue>,
  rulePath: string,
  fieldLabel: string,
  response: JsonObject,
  threshold: number | null,
  matrixRowValue: unknown,
  matrixMeasureId: unknown,
  hasMatrixRowValue: boolean,
  hasMatrixMeasureId: boolean,
  assetsById: Map<string, JsonObject>,
  thresholdHigh?: number,
  requireNotApplicable = false,
): void {
  if (
    !hasMatrixRowValue ||
    typeof matrixRowValue !== "string" ||
    !matrixRowValue
  ) {
    addIssue(
      issues,
      `${rulePath}.matrixRowValue`,
      "visibility_rule_unknown_matrix_row",
      repairLine("visibility_rule_unknown_matrix_row", { fieldLabel: String(fieldLabel), rowValues: [...collectMatrixVisibilityRowValues(response, assetsById)].join(", ") }),
    );
    return;
  }

  const allowedRows = collectMatrixVisibilityRowValues(response, assetsById);
  if (!allowedRows.has(matrixRowValue)) {
    addIssue(
      issues,
      `${rulePath}.matrixRowValue`,
      "visibility_rule_unknown_matrix_row",
      repairLine("visibility_rule_unknown_matrix_row", { fieldLabel: String(fieldLabel), rowValues: [...collectMatrixVisibilityRowValues(response, assetsById)].join(", ") }),
    );
  }

  const measures = asArray(response.measures).filter(isRecord);
  if (measures.length === 0) {
    // The grid's own matrix_measures_too_few refuses a grid with no measure.
    return;
  }

  let selectedMeasure: JsonObject | null = null;
  if (measures.length === 1) {
    const soleMeasure = measures[0];
    if (
      hasMatrixMeasureId &&
      (typeof matrixMeasureId !== "string" ||
        matrixMeasureId !== soleMeasure.id)
    ) {
      addIssue(
        issues,
        `${rulePath}.matrixMeasureId`,
        "visibility_rule_unknown_matrix_measure",
        repairLine("visibility_rule_unknown_matrix_measure", { fieldLabel: String(fieldLabel), measureIds: measures.map((measure) => String(measure.id)).join(", ") }),
      );
      return;
    }
    selectedMeasure = soleMeasure;
  } else {
    if (
      !hasMatrixMeasureId ||
      typeof matrixMeasureId !== "string" ||
      !matrixMeasureId
    ) {
      addIssue(
        issues,
        `${rulePath}.matrixMeasureId`,
        "visibility_rule_unknown_matrix_measure",
        repairLine("visibility_rule_unknown_matrix_measure", { fieldLabel: String(fieldLabel), measureIds: measures.map((measure) => String(measure.id)).join(", ") }),
      );
      return;
    }
    selectedMeasure =
      measures.find((measure) => measure.id === matrixMeasureId) ?? null;
    if (!selectedMeasure) {
      addIssue(
        issues,
        `${rulePath}.matrixMeasureId`,
        "visibility_rule_unknown_matrix_measure",
        repairLine("visibility_rule_unknown_matrix_measure", { fieldLabel: String(fieldLabel), measureIds: measures.map((measure) => String(measure.id)).join(", ") }),
      );
      return;
    }
  }

  if (requireNotApplicable && selectedMeasure.includeNotApplicable !== true) {
    addIssue(
      issues,
      `${rulePath}.matrixMeasureId`,
      "visibility_rule_unsupported_dependency_type",
      repairLine("visibility_rule_unsupported_dependency_type", { fieldLabel: String(fieldLabel), operator: "is_not_applicable", reads: "a matrix_rating measure that offers N/A (includeNotApplicable)", sourceType: "matrix_rating", readers: operatorsReadingText("matrix_rating", "is_not_applicable") }),
    );
    return;
  }

  const unit = selectedMeasure.unit;
  if (typeof unit !== "string" || !isRatingUnitId(unit)) {
    // The draft schema refuses a measure without a rating unit (a required enum).
    return;
  }
  if (threshold === null) {
    return;
  }
  const [min, max] = ratingBounds(unit);
  if (threshold < min || threshold > max) {
    addIssue(
      issues,
      `${rulePath}.threshold`,
      "visibility_rule_threshold_out_of_bounds",
      repairLine("visibility_rule_threshold_out_of_bounds", { fieldLabel: String(fieldLabel), field: "threshold", range: `between ${min} and ${max} on the ${unit} scale` }),
    );
  }
  if (
    typeof thresholdHigh === "number" &&
    (thresholdHigh < min || thresholdHigh > max)
  ) {
    addIssue(
      issues,
      `${rulePath}.thresholdHigh`,
      "visibility_rule_threshold_out_of_bounds",
      repairLine("visibility_rule_threshold_out_of_bounds", { fieldLabel: String(fieldLabel), field: "thresholdHigh", range: `between ${min} and ${max} on the ${unit} scale` }),
    );
  }
}

function validateMatrixCategoricalVisibilityRule(
  issues: Array<QuestionnaireValidationIssue>,
  rulePath: string,
  fieldLabel: string,
  response: JsonObject,
  matrixRowValue: unknown,
  hasMatrixRowValue: boolean,
  assetsById: Map<string, JsonObject>,
): void {
  if (!hasMatrixRowValue || typeof matrixRowValue !== "string" || !matrixRowValue) {
    addIssue(issues, `${rulePath}.matrixRowValue`, "visibility_rule_unknown_matrix_row", repairLine("visibility_rule_unknown_matrix_row", { fieldLabel: String(fieldLabel), rowValues: [...collectMatrixVisibilityRowValues(response, assetsById)].join(", ") }));
    return;
  }
  if (!collectMatrixVisibilityRowValues(response, assetsById).has(matrixRowValue)) {
    addIssue(issues, `${rulePath}.matrixRowValue`, "visibility_rule_unknown_matrix_row", repairLine("visibility_rule_unknown_matrix_row", { fieldLabel: String(fieldLabel), rowValues: [...collectMatrixVisibilityRowValues(response, assetsById)].join(", ") }));
  }
}

export function isVisibilityRuleGroupRecord(rule: unknown): boolean {
  return (
    isRecord(rule) &&
    (rule.join === "and" || rule.join === "or") &&
    Array.isArray(rule.rules)
  );
}

const CHOICES_FROM_MODES = new Set(["selected", "unselected", "all"]);
// Which question types carry answers forward (`choicesFromSource`), and which
// receive them (`choicesFromTarget`): question-capability registry flags.
const QUESTION_CAPABILITY_FACTS = { capabilities: QUESTION_CAPABILITIES };

function optionValuesOf(response: JsonObject): Set<string> {
  return new Set(
    asArray(response.options)
      .filter(isRecord)
      .map((option) => (typeof option.value === "string" ? option.value : ""))
      .filter(Boolean),
  );
}

interface ChoiceOptionMirrorMetadata {
  label: unknown;
  exclusive: boolean;
}

function optionMirrorMetadataOf(
  response: JsonObject,
): Map<string, ChoiceOptionMirrorMetadata> {
  const metadata = new Map<string, ChoiceOptionMirrorMetadata>();
  asArray(response.options)
    .filter(isRecord)
    .forEach((option) => {
      if (typeof option.value !== "string" || !option.value) {
        return;
      }
      metadata.set(option.value, {
        label: option.label,
        exclusive: option.exclusive === true,
      });
    });
  return metadata;
}

function positiveInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : fallback;
}

function nonNegativeInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : fallback;
}

function choicesFromTargetMinimum(
  question: JsonObject,
  response: JsonObject,
  kind: string,
): { count: number; pathSuffix: string } {
  if (kind === "multi_select") {
    return {
      count: positiveInteger(
        response.minSelected,
        question.required === true ? 1 : 0,
      ),
      pathSuffix: "minSelected",
    };
  }
  if (kind === "ranking") {
    const bounds = rankingBounds(response);
    return {
      count:
        bounds.kind === "partial"
          ? bounds.min
          : question.required === true
            ? 1
            : 0,
      pathSuffix: "minRankedItems",
    };
  }
  return { count: 1, pathSuffix: "choicesFrom" };
}

/**
 * Smallest non-empty choice set SurveyJS can expose to the target for any
 * valid source answer. Empty carry-forward sets hide the target, so they do
 * not make its minimum impossible.
 */
function minimumVisibleChoicesFromCount(
  source: JsonObject,
  sourceResponse: JsonObject,
  sourceKind: string,
  mode: string,
  optionCount: number,
): number | null {
  if (optionCount < 1) {
    return null;
  }
  if (mode === "all") {
    return optionCount;
  }
  if (sourceKind === "single_select") {
    const count = mode === "selected" ? 1 : optionCount - 1;
    return count > 0 ? count : null;
  }

  const minimumSelected = positiveInteger(sourceResponse.minSelected, 1);
  const maximumSelected = Math.min(
    nonNegativeInteger(sourceResponse.maxSelected, optionCount),
    optionCount,
  );
  if (mode === "selected") {
    return minimumSelected <= maximumSelected && minimumSelected <= optionCount
      ? minimumSelected
      : null;
  }

  const visibleCounts: Array<number> = [];
  const largestSelectionWithRemainder = Math.min(
    maximumSelected,
    optionCount - 1,
  );
  if (minimumSelected <= largestSelectionWithRemainder) {
    visibleCounts.push(optionCount - largestSelectionWithRemainder);
  }
  if (source.required !== true) {
    // Optional multi-select permits a blank answer even when minSelected is
    // positive; unselected mode then exposes the complete option set.
    visibleCounts.push(optionCount);
  }
  return visibleCounts.length > 0 ? Math.min(...visibleCounts) : null;
}

function choiceKindOf(question: JsonObject): string | undefined {
  const response = responseOf(question);
  if (typeof response.kind === "string") return response.kind;
  return typeof question.type === "string" ? question.type : undefined;
}

/**
 * Whether carrying `source`'s options into `target` with `mode` can meet the
 * target's minimum for every valid source answer: the negation of
 * `choices_from_cardinality_unreachable`, from the same two rules. Editors
 * offer only the modes this accepts.
 */
export function isChoicesFromModeReachable(
  source: unknown,
  target: unknown,
  mode: string,
): boolean {
  if (!isRecord(source) || !isRecord(target)) return false;
  const sourceKind = choiceKindOf(source);
  const targetKind = choiceKindOf(target);
  if (sourceKind === undefined || targetKind === undefined) return false;
  const sourceResponse = responseOf(source);
  const minimumVisibleCount = minimumVisibleChoicesFromCount(
    source,
    sourceResponse,
    sourceKind,
    mode,
    optionValuesOf(sourceResponse).size,
  );
  return (
    minimumVisibleCount === null ||
    choicesFromTargetMinimum(target, responseOf(target), targetKind).count <=
      minimumVisibleCount
  );
}

// Additive choice carry-forward. Compiles to SurveyJS choicesFromQuestion.
function validateQuestionChoicesFrom(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  question: JsonObject,
  index: number,
  questionPath: string,
): void {
  const response = responseOf(question);
  const choicesFrom = response.choicesFrom;
  if (choicesFrom === undefined) {
    return;
  }

  const path = `${questionPath}.response.choicesFrom`;
  // A question with no type reads as "", which no type allows.
  const kind = choiceKindOf(question) ?? "";
  const [targetFailure] = fieldAllowedForType(QUESTION_CAPABILITY_FACTS, {
    questionType: kind,
    capability: "choicesFromTarget",
  });
  if (targetFailure?.reason === "type_not_allowed") {
    addIssue(
      issues,
      path,
      "choices_from_unsupported_target_type",
      repairLine("choices_from_unsupported_target_type", { englishList: String(englishList(targetFailure.types)) }),
    );
    return;
  }

  if (!isRecord(choicesFrom)) {
    addIssue(
      issues,
      path,
      "invalid_choices_from",
      worded("invalid_choices_from", "choicesFrom must be an object."),
    );
    return;
  }

  const mode = choicesFrom.mode;
  if (typeof mode !== "string" || !CHOICES_FROM_MODES.has(mode)) {
    addIssue(
      issues,
      `${path}.mode`,
      "invalid_choices_from",
      worded("invalid_choices_from", "choicesFrom.mode must be 'selected', 'unselected', or 'all'."),
    );
  }

  const sourceStableKey = choicesFrom.sourceStableKey;
  if (typeof sourceStableKey !== "string" || !sourceStableKey) {
    addIssue(
      issues,
      `${path}.sourceStableKey`,
      "invalid_choices_from",
      worded("invalid_choices_from", "choicesFrom.sourceStableKey is required."),
    );
    return;
  }

  const sourceIndex = researchQuestions.findIndex(
    (candidate) =>
      isRecord(candidate) && candidate.stableKey === sourceStableKey,
  );
  if (sourceIndex === -1 || sourceIndex >= index) {
    addIssue(
      issues,
      `${path}.sourceStableKey`,
      "choices_from_invalid_reference",
      repairLine("choices_from_invalid_reference"),
    );
    return;
  }

  const source = researchQuestions[sourceIndex];
  if (!isRecord(source)) {
    return;
  }
  const sourceResponse = responseOf(source);
  const sourceKind = choiceKindOf(source) ?? "";
  const [sourceFailure] = fieldAllowedForType(QUESTION_CAPABILITY_FACTS, {
    questionType: sourceKind,
    capability: "choicesFromSource",
  });
  if (sourceFailure?.reason === "type_not_allowed") {
    addIssue(
      issues,
      `${path}.sourceStableKey`,
      "choices_from_unsupported_source_type",
      repairLine("choices_from_unsupported_source_type", { englishList: String(englishList(sourceFailure.types, "or")) }),
    );
    return;
  }

  const targetValues = optionValuesOf(response);
  const sourceValues = optionValuesOf(sourceResponse);
  // The options this mode carries into this target, as the one derive takes
  // them (`carriedSourceOptions`: no exclusive answer in selected mode or
  // into a ranking).
  const carried = {
    options: carriedSourceOptions(asArray(sourceResponse.options), mode, kind),
  } as JsonObject;
  const carriedValues = optionValuesOf(carried);
  const valueSetsMismatch =
    targetValues.size !== carriedValues.size ||
    [...targetValues].some((value) => !carriedValues.has(value));
  if (valueSetsMismatch) {
    addIssue(
      issues,
      path,
      "choices_from_value_set_mismatch",
      repairLine("choices_from_value_set_mismatch"),
    );
  } else {
    const targetMetadata = optionMirrorMetadataOf(response);
    const sourceMetadata = optionMirrorMetadataOf(carried);
    const metadataMismatch = [...sourceMetadata].some(([value, metadata]) => {
      const target = targetMetadata.get(value);
      return (
        !target ||
        target.label !== metadata.label ||
        target.exclusive !== metadata.exclusive
      );
    });
    if (metadataMismatch) {
      addIssue(
        issues,
        path,
        "choices_from_option_metadata_mismatch",
        repairLine("choices_from_option_metadata_mismatch"),
      );
    }
  }

  if (typeof mode === "string" && CHOICES_FROM_MODES.has(mode)) {
    const targetMinimum = choicesFromTargetMinimum(question, response, kind);
    const minimumVisibleCount = minimumVisibleChoicesFromCount(
      source,
      sourceResponse,
      sourceKind,
      mode,
      sourceValues.size,
    );
    if (
      minimumVisibleCount !== null &&
      targetMinimum.count > minimumVisibleCount
    ) {
      addIssue(
        issues,
        `${questionPath}.response.${targetMinimum.pathSuffix}`,
        "choices_from_cardinality_unreachable",
        repairLine("choices_from_cardinality_unreachable", { mode: String(mode), minimumVisibleCount: String(minimumVisibleCount), count: String(targetMinimum.count) }),
      );
    }
  }
}

// Additive, schema/compiler-level skip-logic / routing capability.
// `visibilityRule` → SurveyJS visibleIf; `routingRule` → SurveyJS triggers.
// Authors never write raw SurveyJS expressions. The registry facts inside
// these rules run through the generic evaluators (`rule-evaluators.ts`), whose
// examples both languages run (questionnaire-rule-evaluator-conformance).
function validateQuestionVisibilityRule(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  question: JsonObject,
  index: number,
  questionPath: string,
  assetsById: Map<string, JsonObject>,
): void {
  const rule = question.visibilityRule;
  if (rule !== undefined) {
    validateConditionRuleExpr(
      issues,
      researchQuestions,
      rule,
      index,
      `${questionPath}.visibilityRule`,
      assetsById,
      "visibilityRule",
      false,
    );
  }
  // Who skips one lane's copy (`QuestionLaneExclusion`) is a condition too,
  // judged by the same rules; where it may sit is `exposureCoherenceIssues`'.
  // One equal to the question's own condition, or to a leaf every respondent
  // shown it meets (an `and` gate's), would hide that copy from everyone.
  const gate = rule === undefined ? [] : [rule, ...(isVisibilityRuleGroupRecord(rule) && isRecord(rule) && rule.join === "and" ? asArray(rule.rules) : [])];
  asArray(question.laneExclusions).forEach((exclusion, exclusionIndex) => {
    const when = isRecord(exclusion) ? exclusion.when : undefined;
    const path = `${questionPath}.laneExclusions[${exclusionIndex}].when`;
    validateConditionRuleExpr(issues, researchQuestions, when, index, path, assetsById, "laneExclusions.when", false);
    if (when !== undefined && gate.some((condition) => canonicalJson(condition) === canonicalJson(when))) {
      addIssue(issues, path, "lane_exclusion_same_as_gate", repairLine("lane_exclusion_same_as_gate"));
    }
  });
}

function validateQuestionRoutingRule(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  question: JsonObject,
  index: number,
  questionPath: string,
  assetsById: Map<string, JsonObject>,
): void {
  if (question.routingRule === undefined) {
    return;
  }
  validateRoutingRule(
    issues,
    researchQuestions,
    question.routingRule,
    index,
    `${questionPath}.routingRule`,
    assetsById,
  );
}

/**
 * The one TS judge of a condition: every rule a visibility condition, or a
 * route's `when`, must satisfy against the question list it sits in (the
 * source is available at that point; the operator, its fields, value count,
 * threshold, scale, rank and matrix cell fit the source). Each broken rule
 * is its own code. The draft validator, the backend resolver
 * (`runtime.ts`), planBatch and the compiler all read this one function.
 *
 * `hostIndex` is the question the condition sits on; a route's `when`
 * (`allowSelfOrEarlier`) may read that question itself.
 */
export function conditionExprIssues(
  rule: unknown,
  researchQuestions: Array<unknown>,
  options: {
    hostIndex: number;
    allowSelfOrEarlier: boolean;
    assetsById: Map<string, JsonObject>;
    path: string;
  },
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  validateConditionRuleExpr(
    issues,
    researchQuestions,
    rule,
    options.hostIndex,
    options.path,
    options.assetsById,
    options.allowSelfOrEarlier ? "routingRule.when" : "visibilityRule",
    options.allowSelfOrEarlier,
  );
  return issues;
}

/** `conditionExprIssues` for a whole route: its action, target and `when`. */
export function routingRuleIssues(
  rule: unknown,
  researchQuestions: Array<unknown>,
  options: {
    hostIndex: number;
    assetsById: Map<string, JsonObject>;
    path: string;
  },
): Array<QuestionnaireValidationIssue> {
  const issues: Array<QuestionnaireValidationIssue> = [];
  validateRoutingRule(
    issues,
    researchQuestions,
    rule,
    options.hostIndex,
    options.path,
    options.assetsById,
  );
  return issues;
}

function validateRoutingRule(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  rule: unknown,
  index: number,
  rulePath: string,
  assetsById: Map<string, JsonObject>,
): void {
  if (!isRecord(rule)) {
    addIssue(
      issues,
      rulePath,
      "invalid_routing_rule",
      worded("invalid_routing_rule", "routingRule must be an object."),
    );
    return;
  }

  const action = rule.action;
  if (!(ROUTING_ACTIONS as ReadonlyArray<unknown>).includes(action)) {
    addIssue(
      issues,
      `${rulePath}.action`,
      "invalid_routing_rule",
      worded("invalid_routing_rule", "routingRule.action must be 'skip_to' or 'end_survey'."),
    );
    return;
  }

  if ((rule.ruleId != null && (typeof rule.ruleId !== "string" || !rule.ruleId.trim())) ||
      (rule.scope != null && !(ROUTING_SCOPES as ReadonlyArray<unknown>).includes(rule.scope)) ||
      (rule.disposition != null && !(ROUTING_DISPOSITIONS as ReadonlyArray<unknown>).includes(rule.disposition)) ||
      (action === "skip_to" && rule.disposition != null) ||
      // An end is a screen-out or a completed branch in the report; it states
      // which (owner, 2026-10-21), as PLAN's routes already must.
      (action === "end_survey" && rule.disposition == null)) {
    addIssue(issues, rulePath, "invalid_routing_rule", worded("invalid_routing_rule", "Invalid route identity, scope or terminal disposition."));
  }

  const targetStableKey = rule.targetStableKey;
  if (action === "skip_to") {
    if (typeof targetStableKey !== "string" || !targetStableKey) {
      addIssue(
        issues,
        `${rulePath}.targetStableKey`,
        "invalid_routing_rule",
        worded("invalid_routing_rule", "routingRule.targetStableKey is required when action is 'skip_to'."),
      );
    } else {
      const targetIndex = researchQuestions.findIndex(
        (candidate) =>
          isRecord(candidate) && candidate.stableKey === targetStableKey,
      );
      if (targetIndex === -1 || targetIndex <= index) {
        addIssue(
          issues,
          `${rulePath}.targetStableKey`,
          "routing_rule_invalid_target",
          repairLine("routing_rule_invalid_target"),
        );
      } else if (targetIndex === index + 1) {
        // A skip lands on its target. The next research question is the next
        // page, so this route skips nothing.
        addIssue(
          issues,
          `${rulePath}.targetStableKey`,
          "routing_rule_noop_target",
          repairLine("routing_rule_noop_target"),
        );
      }
    }
  } else if (targetStableKey !== undefined && targetStableKey !== null) {
    addIssue(
      issues,
      `${rulePath}.targetStableKey`,
      "invalid_routing_rule",
      worded("invalid_routing_rule", "routingRule.targetStableKey must be omitted when action is 'end_survey'."),
    );
  }

  if (rule.when === undefined) {
    addIssue(
      issues,
      `${rulePath}.when`,
      "invalid_routing_rule",
      worded("invalid_routing_rule", "routingRule.when is required."),
    );
    return;
  }

  validateConditionRuleExpr(
    issues,
    researchQuestions,
    rule.when,
    index,
    `${rulePath}.when`,
    assetsById,
    "routingRule.when",
    true,
  );
}

const CONDITION_FACTS = {
  operators: CONDITION_OPERATOR_FAMILIES,
  sourceFields: CONDITION_SOURCE_FIELDS,
};

/** "rating or matrix_rating": the question types a family's operators read. */
function sourceTypesText(family: ConditionOperatorFamily): string {
  return englishList([...CONDITION_OPERATOR_FAMILIES[family].sourceTypes], "or");
}

/**
 * The quoted operators the registry lets read a `sourceType`
 * source, `except` the one a condition used; "none" when no operator reads it.
 */
function operatorsReadingText(sourceType: string, except: string): string {
  const readers = Object.values(CONDITION_OPERATOR_FAMILIES).flatMap((family) =>
    family.sourceTypes.includes(sourceType)
      ? Object.entries(family.operators)
          .filter(([operator, facts]) => operator !== except && (facts.sourceTypes === undefined || facts.sourceTypes.includes(sourceType)))
          .map(([operator]) => `'${operator}'`)
      : [],
  );
  return readers.length > 0 ? readers.join(", ") : "none";
}

/** "'equals'/'not_equals'": a family's operators that name exactly one value. */
function oneValueOperatorsText(family: ConditionOperatorFamily): string {
  const { values, operators } = CONDITION_OPERATOR_FAMILIES[family];
  return Object.keys(operators)
    .filter((operator) => (operators[operator].values ?? values) === "one")
    .map((operator) => `'${operator}'`)
    .join("/");
}

function validateConditionRuleExpr(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  rule: unknown,
  index: number,
  rulePath: string,
  assetsById: Map<string, JsonObject>,
  fieldLabel: string,
  allowSelfOrEarlier: boolean,
): void {
  if (!isRecord(rule)) {
    addIssue(
      issues,
      rulePath,
      "visibility_rule_malformed",
      worded("visibility_rule_malformed", `${fieldLabel} must be an object.`),
    );
    return;
  }

  if (isVisibilityRuleGroupRecord(rule)) {
    if (rule.join !== "and" && rule.join !== "or") {
      addIssue(
        issues,
        `${rulePath}.join`,
        "visibility_rule_malformed",
        worded("visibility_rule_malformed", `${fieldLabel}.join must be 'and' or 'or'.`),
      );
      return;
    }
    const rules = asArray(rule.rules);
    const [groupSize] = countWithinBounds(
      { min: MIN_VISIBILITY_RULE_GROUP_SIZE, max: MAX_VISIBILITY_RULE_GROUP_SIZE },
      { count: rules.length },
    );
    if (groupSize?.reason === "below_min" || groupSize?.reason === "above_max") {
      addIssue(
        issues,
        `${rulePath}.rules`,
        "visibility_rule_group_size",
        worded(
          "visibility_rule_group_size",
          `${fieldLabel}.rules must join ${MIN_VISIBILITY_RULE_GROUP_SIZE} to ${MAX_VISIBILITY_RULE_GROUP_SIZE} leaf rules.`,
        ),
      );
      return;
    }
    rules.forEach((leaf, leafIndex) => {
      const leafPath = `${rulePath}.rules[${leafIndex}]`;
      if (!isRecord(leaf)) {
        addIssue(
          issues,
          leafPath,
          "visibility_rule_malformed",
          worded("visibility_rule_malformed", `${fieldLabel}.rules entries must be leaf visibility rules.`),
        );
        return;
      }
      if (isVisibilityRuleGroupRecord(leaf)) {
        addIssue(
          issues,
          leafPath,
          "visibility_rule_malformed",
          worded("visibility_rule_malformed", `${fieldLabel}.rules must not contain nested groups.`),
        );
        return;
      }
      validateQuestionVisibilityRuleLeaf(
        issues,
        researchQuestions,
        leaf,
        index,
        leafPath,
        assetsById,
        fieldLabel,
        allowSelfOrEarlier,
      );
    });
    return;
  }

  validateQuestionVisibilityRuleLeaf(
    issues,
    researchQuestions,
    rule,
    index,
    rulePath,
    assetsById,
    fieldLabel,
    allowSelfOrEarlier,
  );
}

function validateQuestionVisibilityRuleLeaf(
  issues: Array<QuestionnaireValidationIssue>,
  researchQuestions: Array<unknown>,
  rule: JsonObject,
  index: number,
  rulePath: string,
  assetsById: Map<string, JsonObject>,
  fieldLabel: string,
  allowSelfOrEarlier: boolean,
): void {
  const dependsOnStableKey = rule.dependsOnStableKey;
  const operator = rule.operator;
  const values = asArray(rule.values);
  const threshold = rule.threshold;
  const thresholdHigh = rule.thresholdHigh;
  const matrixRowValue = rule.matrixRowValue;
  const matrixMeasureId = rule.matrixMeasureId;
  const rankK = rule.rankK;
  const hasMatrixRowValue =
    matrixRowValue !== undefined && matrixRowValue !== null;
  const hasMatrixMeasureId =
    matrixMeasureId !== undefined && matrixMeasureId !== null;
  const operatorFamily = conditionOperatorFamily(operator);
  const isNumericOperator = operatorFamily === "numeric";
  const isChoiceOperator = operatorFamily === "choice";
  const isRankingOperator = operatorFamily === "ranking";
  const isNotApplicableOperator = operatorFamily === "not_applicable";

  if (typeof dependsOnStableKey !== "string" || !dependsOnStableKey) {
    addIssue(
      issues,
      `${rulePath}.dependsOnStableKey`,
      "visibility_rule_malformed",
      worded("visibility_rule_malformed", `${fieldLabel}.dependsOnStableKey must be a non-empty string.`),
    );
    return;
  }

  const [unknownOperator] = operatorReadsSource(CONDITION_FACTS, { operator });
  if (unknownOperator?.reason === "unknown_operator") {
    addIssue(
      issues,
      `${rulePath}.operator`,
      "visibility_rule_unknown_operator",
      repairLine("visibility_rule_unknown_operator", { fieldLabel: String(fieldLabel), operatorsValue: String(unknownOperator.operators.join(", ")) }),
    );
    return;
  }

  // Visibility: strictly earlier. Routing when: host question or earlier.
  const dependencyIndex = researchQuestions.findIndex(
    (candidate) =>
      isRecord(candidate) && candidate.stableKey === dependsOnStableKey,
  );

  const dependencyOutOfRange = allowSelfOrEarlier
    ? dependencyIndex === -1 || dependencyIndex > index
    : dependencyIndex === -1 || dependencyIndex >= index;

  if (dependencyOutOfRange) {
    addIssue(
      issues,
      `${rulePath}.dependsOnStableKey`,
      "visibility_rule_invalid_reference",
      worded("visibility_rule_invalid_reference", allowSelfOrEarlier
        ? `${fieldLabel}.dependsOnStableKey must reference this question or an earlier question in researchQuestions.`
        : `${fieldLabel}.dependsOnStableKey must reference an earlier question in researchQuestions.`),
    );
    return;
  }

  const dependency = researchQuestions[dependencyIndex];
  if (!isRecord(dependency)) {
    return;
  }
  // The registry facts of this leaf: whether its operator reads the
  // dependency's type, and names as many values as its family says.
  const operatorFailures = operatorReadsSource(CONDITION_FACTS, {
    operator,
    sourceType: dependency.type,
    valueCount: values.length,
  });
  const valueCountFailure = operatorFailures.find(
    (failure) => failure.reason === "value_count",
  );
  const familySourceFailure = operatorFailures.find(
    (failure) => failure.reason === "family_source_type",
  );
  const operatorSourceFailure = operatorFailures.find(
    (failure) => failure.reason === "operator_source_type",
  );
  // Each field the leaf sets is one its operator or its source reads (the
  // registry's `fields` and `sourceFields`, the generic evaluator's verdict).
  const fieldFailures = conditionFieldsRead(CONDITION_FACTS, {
    operator,
    sourceType: dependency.type,
    fields: CONDITION_FIELDS.filter((field) => rule[field] !== undefined && rule[field] !== null),
  });
  for (let at = 0; at < fieldFailures.length; at += 1) {
    const failure = fieldFailures[at];
    if (failure.reason === "field_not_read") {
      addIssue(
        issues,
        `${rulePath}.${failure.field}`,
        "visibility_rule_field_not_for_operator",
        repairLine("visibility_rule_field_not_for_operator", { fieldLabel: String(fieldLabel), field: failure.field, operator: failure.operator }),
      );
    }
  }
  if (valueCountFailure && (isNumericOperator || isNotApplicableOperator)) {
    addIssue(
      issues,
      `${rulePath}.values`,
      "visibility_rule_field_not_for_operator",
      repairLine("visibility_rule_field_not_for_operator", { fieldLabel: String(fieldLabel), field: "values", operator: String(operator) }),
    );
  }

  if (isNotApplicableOperator) {
    const response = responseOf(dependency);
    if (dependency.type === "matrix_rating") {
      validateMatrixRatingVisibilityRule(
        issues, rulePath, fieldLabel, response, null, matrixRowValue,
        matrixMeasureId, hasMatrixRowValue, hasMatrixMeasureId, assetsById,
        undefined, true,
      );
      return;
    }
    if (familySourceFailure || response.includeNotApplicable !== true) {
      addIssue(issues, `${rulePath}.dependsOnStableKey`, "visibility_rule_unsupported_dependency_type", repairLine("visibility_rule_unsupported_dependency_type", { fieldLabel: String(fieldLabel), operator: "is_not_applicable", reads: `a ${sourceTypesText("not_applicable")} question that offers N/A (includeNotApplicable)`, sourceType: String(dependency.type), readers: operatorsReadingText(String(dependency.type), "is_not_applicable") }));
    }
    return;
  }

  if (isNumericOperator) {
    if (typeof threshold !== "number" || !Number.isFinite(threshold)) {
      addIssue(
        issues,
        `${rulePath}.threshold`,
        "visibility_rule_threshold_invalid",
        worded("visibility_rule_threshold_invalid", `${fieldLabel}.threshold must be a finite number for numeric operators (${NUMERIC_CONDITION_OPERATORS.join("/")}).`),
      );
      return;
    }
    if (operator === "between") {
      if (
        typeof thresholdHigh !== "number" ||
        !Number.isFinite(thresholdHigh)
      ) {
        addIssue(
          issues,
          `${rulePath}.thresholdHigh`,
          "visibility_rule_threshold_invalid",
          worded("visibility_rule_threshold_invalid", `${fieldLabel}.thresholdHigh must be a finite number for 'between'.`),
        );
        return;
      }
      if (threshold > thresholdHigh) {
        addIssue(
          issues,
          `${rulePath}.thresholdHigh`,
          "visibility_rule_threshold_invalid",
          worded("visibility_rule_threshold_invalid", `${fieldLabel}.threshold must be less than or equal to thresholdHigh for 'between'.`),
        );
      }
    }
    if (familySourceFailure?.reason === "family_source_type") {
      addIssue(
        issues,
        `${rulePath}.dependsOnStableKey`,
        "visibility_rule_unsupported_dependency_type",
        repairLine("visibility_rule_unsupported_dependency_type", { fieldLabel: String(fieldLabel), operator: String(operator), reads: `${englishList(familySourceFailure.sourceTypes, "or")} questions`, sourceType: String(dependency.type), readers: operatorsReadingText(String(dependency.type), String(operator)) }),
      );
      return;
    }
    const response = responseOf(dependency);
    const betweenHigh =
      operator === "between" &&
      typeof thresholdHigh === "number" &&
      Number.isFinite(thresholdHigh)
        ? thresholdHigh
        : undefined;
    if (dependency.type === "matrix_rating") {
      validateMatrixRatingVisibilityRule(
        issues,
        rulePath,
        fieldLabel,
        response,
        threshold,
        matrixRowValue,
        matrixMeasureId,
        hasMatrixRowValue,
        hasMatrixMeasureId,
        assetsById,
        betweenHigh,
      );
      return;
    }
    if (dependency.type === "rating") {
      const unit = response.unit;
      if (typeof unit !== "string" || !isRatingUnitId(unit)) {
        // The draft schema refuses a rating without a rating unit (a required enum).
        return;
      }
      const [min, max] = ratingBounds(unit);
      if (threshold < min || threshold > max) {
        addIssue(
          issues,
          `${rulePath}.threshold`,
          "visibility_rule_threshold_out_of_bounds",
          repairLine("visibility_rule_threshold_out_of_bounds", { fieldLabel: String(fieldLabel), field: "threshold", range: `between ${min} and ${max} on the ${unit} scale` }),
        );
      }
      if (
        typeof betweenHigh === "number" &&
        (betweenHigh < min || betweenHigh > max)
      ) {
        addIssue(
          issues,
          `${rulePath}.thresholdHigh`,
          "visibility_rule_threshold_out_of_bounds",
          repairLine("visibility_rule_threshold_out_of_bounds", { fieldLabel: String(fieldLabel), field: "thresholdHigh", range: `between ${min} and ${max} on the ${unit} scale` }),
        );
      }
      return;
    }

    const unit = response.unit;
    if (typeof unit !== "string" || !isContinuousUnitId(unit)) {
      // The draft schema refuses a continuous question without its unit (a required enum).
      return;
    }
    const bounds = continuousThresholdBounds(response);
    if (bounds) {
      const [min, max] = bounds;
      const rangeLabel =
        Number.isFinite(min) && Number.isFinite(max)
          ? `between ${min} and ${max}`
          : Number.isFinite(min)
            ? `>= ${min}`
            : `<= ${max}`;
      if (threshold < min || threshold > max) {
        addIssue(
          issues,
          `${rulePath}.threshold`,
          "visibility_rule_threshold_out_of_bounds",
          repairLine("visibility_rule_threshold_out_of_bounds", { fieldLabel: String(fieldLabel), field: "threshold", range: rangeLabel }),
        );
      }
      if (
        typeof betweenHigh === "number" &&
        (betweenHigh < min || betweenHigh > max)
      ) {
        addIssue(
          issues,
          `${rulePath}.thresholdHigh`,
          "visibility_rule_threshold_out_of_bounds",
          repairLine("visibility_rule_threshold_out_of_bounds", { fieldLabel: String(fieldLabel), field: "thresholdHigh", range: rangeLabel }),
        );
      }
    }
    return;
  }

  if (isRankingOperator) {
    if (typeof rankK !== "number" || !Number.isInteger(rankK) || rankK < 1) {
      addIssue(
        issues,
        `${rulePath}.rankK`,
        "visibility_rule_rank_k_out_of_bounds",
        repairLine("visibility_rule_rank_k_out_of_bounds", { fieldLabel: String(fieldLabel), range: "of 1 or more" }),
      );
      return;
    }
    if (
      values.length === 0 ||
      !values.every((value) => typeof value === "string" && value.length > 0)
    ) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_values_missing",
        repairLine("visibility_rule_values_missing", { fieldLabel: String(fieldLabel) }),
      );
      return;
    }
    if (valueCountFailure) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_value_count",
        repairLine("visibility_rule_value_count", { fieldLabel: String(fieldLabel), oneValueOperatorsText: String(oneValueOperatorsText("ranking")) }),
      );
    }
    if (familySourceFailure?.reason === "family_source_type") {
      addIssue(
        issues,
        `${rulePath}.dependsOnStableKey`,
        "visibility_rule_unsupported_dependency_type",
        repairLine("visibility_rule_unsupported_dependency_type", { fieldLabel: String(fieldLabel), operator: String(operator), reads: `${englishList(familySourceFailure.sourceTypes, "or")} questions`, sourceType: String(dependency.type), readers: operatorsReadingText(String(dependency.type), String(operator)) }),
      );
      return;
    }
    const response = responseOf(dependency);
    const assumedLength = rankingMaxLength(response);
    if (assumedLength < 1) {
      // The ranking's own ranking_options_too_few refuses a ranking with no option.
      return;
    }
    if (rankK > assumedLength) {
      addIssue(
        issues,
        `${rulePath}.rankK`,
        "visibility_rule_rank_k_out_of_bounds",
        repairLine("visibility_rule_rank_k_out_of_bounds", { fieldLabel: String(fieldLabel), range: `from 1 to ${assumedLength}` }),
      );
    }
    if (operator === "rank_in_bottom" && rankingFixedLength(response) === null) {
      addIssue(
        issues,
        `${rulePath}.operator`,
        "visibility_rule_rank_bottom_variable_length",
        repairLine("visibility_rule_rank_bottom_variable_length", { fieldLabel: String(fieldLabel) }),
      );
    }
    const optionValues = new Set(
      asArray(response.options)
        .filter(isRecord)
        .map((option) => option.value)
        .filter((value): value is string => typeof value === "string"),
    );
    const unknownValues = values.filter(
      (value) =>
        typeof value !== "string" ||
        !optionValues.has(value),
    );
    if (unknownValues.length > 0) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_unknown_value",
        repairLine("visibility_rule_unknown_value", { fieldLabel: String(fieldLabel), unknownValues: String(unknownValues.join(", ")) }),
      );
    }
    return;
  }

  if (isChoiceOperator) {
    const matrixCategorical = dependency.type === "matrix_categorical";
    if (
      values.length === 0 ||
      !values.every((value) => typeof value === "string" && value.length > 0)
    ) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_values_missing",
        repairLine("visibility_rule_values_missing", { fieldLabel: String(fieldLabel) }),
      );
      return;
    }

    if (valueCountFailure) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_value_count",
        repairLine("visibility_rule_value_count", { fieldLabel: String(fieldLabel), oneValueOperatorsText: String(oneValueOperatorsText("choice")) }),
      );
    }

    if (familySourceFailure?.reason === "family_source_type") {
      addIssue(
        issues,
        `${rulePath}.dependsOnStableKey`,
        "visibility_rule_unsupported_dependency_type",
        repairLine("visibility_rule_unsupported_dependency_type", { fieldLabel: String(fieldLabel), operator: String(operator), reads: `${englishList(familySourceFailure.sourceTypes, "or")} questions`, sourceType: String(dependency.type), readers: operatorsReadingText(String(dependency.type), String(operator)) }),
      );
      return;
    }

    // equals/not_equals compile to scalar equality (!=); on multi_select the
    // compiler would otherwise silently emit anyof/notcontains (membership).
    // An operator that reads one answer names the source types it reads, and
    // its set operator (any_of/none_of) reads a multi select's answer set.
    if (operatorSourceFailure?.reason === "operator_source_type") {
      addIssue(
        issues,
        `${rulePath}.operator`,
        "visibility_rule_unsupported_dependency_type",
        repairLine("visibility_rule_unsupported_dependency_type", { fieldLabel: String(fieldLabel), operator: String(operatorSourceFailure.operator), reads: `${englishList(operatorSourceFailure.sourceTypes, "or")} questions`, sourceType: String(dependency.type), readers: operatorsReadingText(String(dependency.type), String(operatorSourceFailure.operator)) }),
      );
      return;
    }

    if (matrixCategorical) {
      validateMatrixCategoricalVisibilityRule(
        issues,
        rulePath,
        fieldLabel,
        responseOf(dependency),
        matrixRowValue,
        hasMatrixRowValue,
        assetsById,
      );
    }
    const optionValues = new Set(
      asArray(
        matrixCategorical
          ? responseOf(dependency).categories
          : responseOf(dependency).options,
      )
        .filter(isRecord)
        .map((option) => option.value)
        .filter((value): value is string => typeof value === "string"),
    );

    const unknownValues = values.filter(
      (value) =>
        typeof value !== "string" ||
        !optionValues.has(value),
    );
    if (unknownValues.length > 0) {
      addIssue(
        issues,
        `${rulePath}.values`,
        "visibility_rule_unknown_value",
        repairLine("visibility_rule_unknown_value", { fieldLabel: String(fieldLabel), unknownValues: String(unknownValues.join(", ")) }),
      );
    }
  }
}

/** A template token: `{{` and `}}` around one identifier. */
const TEMPLATE_PLACEHOLDER = /\{\{\s*[A-Za-z0-9_]+\s*\}\}/;

/**
 * Each text a respondent reads in one question, with its path: its title,
 * description and every string of its response (options, rows, measures,
 * anchors, point labels). Stable IDs, values, references and routing
 * metadata are identities, not text respondents read.
 */
function forEachRespondentText(
  question: JsonObject,
  questionPath: string,
  visit: (value: unknown, path: string) => void,
): void {
  const checkFields = (
    record: JsonObject,
    path: string,
    fields: string[],
  ): void => {
    fields.forEach((field) => visit(record[field], `${path}.${field}`));
  };
  const checkItems = (items: unknown, path: string): void => {
    asArray(items).forEach((item, index) => {
      if (isRecord(item))
        checkFields(item, `${path}[${index}]`, ["label", "description"]);
    });
  };
  const checkRatingCopy = (rating: JsonObject, path: string): void => {
    if (isRecord(rating.anchors)) {
      checkFields(rating.anchors, `${path}.anchors`, [
        "minLabel",
        "maxLabel",
        // A midpoint reaches respondents only where the unit has one.
        ...(ratingHasMidpoint(rating.unit) ? ["midpointLabel"] : []),
      ]);
    }
    asArray(rating.pointLabels).forEach((label, index) =>
      visit(label, `${path}.pointLabels[${index}]`),
    );
    visit(rating.notApplicableLabel, `${path}.notApplicableLabel`);
  };
  checkFields(question, questionPath, ["title", "description"]);
  if (!isRecord(question.response)) return;
  const response = question.response;
  const responsePath = `${questionPath}.response`;
  switch (response.kind) {
    case "single_select":
    case "multi_select":
    case "ranking":
      checkItems(response.options, `${responsePath}.options`);
      break;
    case "rating":
      checkRatingCopy(response, responsePath);
      break;
    case "matrix_rating":
    case "matrix_categorical":
      if (isRecord(response.rows) && response.rows.source === "fixed") {
        checkItems(response.rows.rows, `${responsePath}.rows.rows`);
      }
      if (response.kind === "matrix_categorical") {
        checkItems(response.categories, `${responsePath}.categories`);
      } else {
        asArray(response.measures).forEach((measure, index) => {
          if (!isRecord(measure)) return;
          const path = `${responsePath}.measures[${index}]`;
          visit(measure.label, `${path}.label`);
          checkRatingCopy(measure, path);
        });
      }
      break;
    case "open_end":
      checkFields(response, responsePath, ["placeholder"]);
      break;
  }
}

/**
 * Text a respondent reads never carries an unfilled template token such as
 * `{{brand}}`: twins would read the braces. A literal token check on the
 * draft, so Review, Save and the chat repair refuse it with its code before
 * compile does.
 */
function validateTemplatePlaceholders(
  issues: Array<QuestionnaireValidationIssue>,
  question: JsonObject,
  questionPath: string,
): void {
  forEachRespondentText(question, questionPath, (value, path) => {
    if (typeof value !== "string") return;
    const placeholder = value.match(TEMPLATE_PLACEHOLDER)?.[0];
    if (placeholder) {
      addIssue(
        issues,
        path,
        "respondent_text_template_placeholder",
        repairLine("respondent_text_template_placeholder", {
          placeholder: String(placeholder),
        }),
      );
    }
  });
}

/**
 * Every token in a stored question's text names an asset of the catalog, and
 * no asset is named by `@` and its exact name instead of its token: the two
 * rules' generic evaluators (`assetTokensKnown`, `assetNamesTokenized`; the
 * same in Python, where AUTHOR repairs them). Read on the stored text, before
 * tokens are rendered: a model writes the token it was shown, and the Save
 * binds the builder's typed `@Name` before the draft is validated. Any other
 * `@` (a social handle) is plain text (owner, 2026-10-22).
 */
function validateAssetMentions(
  issues: Array<QuestionnaireValidationIssue>,
  stored: unknown,
  questionPath: string,
  catalog: { keys: ReadonlyArray<string>; names: ReadonlyArray<string> },
): void {
  if (!isRecord(stored)) return;
  forEachRespondentText(stored, questionPath, (text, path) => {
    const failures = [
      ...assetTokensKnown({}, { text, keys: catalog.keys }),
      ...assetNamesTokenized({}, { text, names: catalog.names }),
    ];
    for (let at = 0; at < failures.length; at += 1) {
      const failure = failures[at];
      if (failure.reason === "key_unknown") {
        addIssue(issues, path, "asset_mention_key_unknown", repairLine("asset_mention_key_unknown", { token: failure.token }));
      } else if (failure.reason === "name_not_token") {
        addIssue(issues, path, "asset_mention_not_token", repairLine("asset_mention_not_token", { name: failure.name }));
      }
    }
  });
}

function validateDisallowedLogic(
  issues: Array<QuestionnaireValidationIssue>,
  value: unknown,
  path: string,
): void {
  if (!isRecord(value) && !Array.isArray(value)) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      validateDisallowedLogic(issues, item, `${path}[${index}]`),
    );
    return;
  }

  // Tested assets are opaque stimulus data, not authored SurveyJS config:
  // their metadata may legitimately carry keys such as `expression`.
  if (path === "testedAssets") {
    return;
  }

  [
    "visibleIf",
    "enableIf",
    "triggers",
    "calculatedValues",
    "branching",
    "piping",
    "expression",
  ].forEach((key) => {
    if (key in value) {
      addIssue(
        issues,
        path ? `${path}.${key}` : key,
        "advanced_logic_disallowed",
        repairLine("advanced_logic_disallowed"),
      );
    }
  });

  Object.entries(value).forEach(([key, childValue]) => {
    validateDisallowedLogic(issues, childValue, path ? `${path}.${key}` : key);
  });
}

/** The held-back fact an unaided brand read is asked without (wave D, D2). */
const BRAND_FACT = "brand";

/**
 * A question that tells the brand an unaided brand read must be asked
 * without: a list or grid derived from the brand roster, or a lift pre half.
 */
function showsBrand(question: Record<string, unknown>): boolean {
  const response = isRecord(question.response) ? question.response : null;
  return (
    (isRecord(response?.optionSource) && response.optionSource.kind === "brand_roster") ||
    (isRecord(response?.rows) && response.rows.derivedFrom === "brand_roster") ||
    asArray(question.tags).some((tag) => readLiftTag(tag)?.half === "pre")
  );
}

/**
 * Held-back facts in the stored questionnaire (wave D, D2): a question asked
 * without a fact comes before the first question that tells it. The facts are
 * typed: PLAN's (`planAttribution.answeredWithout` / `reveals`), and the brand,
 * which a question of a recipe module that reads it unprompted
 * (`brandUndisclosedModuleIds`) is asked without and a question that shows it
 * (`showsBrand`) tells. Re-run on every write, so a user's drag, a deleted
 * reveal or a chat insert is said; the catalog entry's writer split refuses a
 * model batch and shows a user's edit.
 *
 * One issue per fact, on the question that tells it: it is the one that has
 * to move, and the reads it comes before are the ones a person cannot act on
 * alone (CT-10: five refusals listed Q1 to Q7, all before the exposure, with
 * "Move it above it", while the price they were asked without sat in Q9). A
 * fact no question tells is no read's to move above: it is its own warning.
 */
export function heldBackReadIssues(
  questions: ReadonlyArray<unknown>,
  brandUndisclosedModuleIds: ReadonlyArray<string> = [],
): Array<QuestionnaireValidationIssue> {
  const brandReads = new Set(brandUndisclosedModuleIds);
  const keys = (question: unknown, field: "answeredWithout" | "reveals"): Array<string> => {
    if (!isRecord(question)) return [];
    const attribution = isRecord(question.planAttribution) ? question.planAttribution : {};
    const typed = asArray(attribution[field]).filter((key): key is string => typeof key === "string");
    const brand =
      field === "reveals"
        ? showsBrand(question)
        : typeof attribution.recipeModuleId === "string" && brandReads.has(attribution.recipeModuleId);
    return brand ? [...typed, BRAND_FACT] : typed;
  };
  const keysTold = (question: unknown): Array<string> => {
    const attribution = isRecord(question) && isRecord(question.planAttribution) ? question.planAttribution : {};
    return asArray(attribution.reveals).filter((key): key is string => typeof key === "string");
  };
  const stableKeyAt = (index: number): string => {
    const question = questions[index];
    return isRecord(question) ? String(question.stableKey ?? index) : String(index);
  };
  const revealedAt = new Map<string, number>();
  questions.forEach((question, index) => {
    for (const key of keys(question, "reveals")) {
      if (!revealedAt.has(key)) revealedAt.set(key, index);
    }
  });
  const issues: Array<QuestionnaireValidationIssue> = [];
  // For each fact a question tells: the last read asked without it that comes
  // at or after that question, so placing the revealer after it clears them all.
  const lastReadAfter = new Map<string, number>();
  questions.forEach((question, index) => {
    for (const key of keys(question, "answeredWithout")) {
      const revealIndex = revealedAt.get(key);
      if (revealIndex === undefined) {
        // The brand need not be told at all; any other fact is no longer told.
        if (key !== BRAND_FACT) {
          addIssue(
            issues,
            `researchQuestions[${index}].planAttribution.answeredWithout`,
            "held_back_fact_not_told",
            repairLine("held_back_fact_not_told", { questionKey: stableKeyAt(index), factKey: key }),
          );
        }
      } else if (revealIndex <= index) {
        lastReadAfter.set(key, index);
      }
    }
  });
  lastReadAfter.forEach((readIndex, key) => {
    const revealIndex = revealedAt.get(key) as number;
    // The brand is told by what the question shows, not by a typed key.
    const typed = isRecord(questions[revealIndex]) && keysTold(questions[revealIndex]).includes(key);
    addIssue(
      issues,
      typed
        ? `researchQuestions[${revealIndex}].planAttribution.reveals`
        : `researchQuestions[${revealIndex}]`,
      "held_back_read_after_reveal",
      repairLine("held_back_read_after_reveal", {
        questionKey: stableKeyAt(readIndex),
        factKey: key,
        revealKey: stableKeyAt(revealIndex),
      }),
    );
  });
  return issues;
}

export function validateQuestionnaireDraftPayload(
  stored: unknown,
  policy: { researchQuestionCeiling?: number } = {},
): DraftValidationResult {
  const issues: Array<QuestionnaireValidationIssue> = [];
  // The text rules read the text respondents and the deck are given: each
  // stored asset token as its asset's `@Name` (D2 S3.1). Identity is read
  // from typed refs, never from the text. Only the mention rule reads the
  // stored tokens (`validateAssetMentions`).
  const payload = isRecord(stored)
    ? renderAssetRefTokens(stored, stored.testedAssets)
    : stored;
  if (!isRecord(payload)) {
    addIssue(
      issues,
      "",
      "draft_payload_invalid",
      worded("draft_payload_invalid", "Questionnaire draft payload must be an object."),
    );
    return { status: "invalid", issues };
  }

  const seenStableKeys = new Map<string, string>();

  const researchQuestions = asArray(payload.researchQuestions);
  const [questionCount] = countWithinBounds(
    {
      min: resolveResearchQuestionFloor(payload),
      max: Math.min(RESEARCH_QUESTION_CEILING, policy.researchQuestionCeiling ?? RESEARCH_QUESTION_CEILING),
    },
    { count: researchQuestions.length },
  );
  if (questionCount?.reason === "below_min") {
    addIssue(
      issues,
      "researchQuestions",
      "research_questions_too_few",
      repairLine("research_questions_too_few", { minValue: String(questionCount.min) }),
    );
  }
  if (questionCount?.reason === "above_max") {
    addIssue(
      issues,
      "researchQuestions",
      "research_questions_too_many",
      repairLine("research_questions_too_many", { max: String(questionCount.max) }),
      policy.researchQuestionCeiling !== undefined ? { ceiling: questionCount.max } : undefined,
    );
  }
  if (!Array.isArray(payload.researchQuestions)) {
    addIssue(
      issues,
      "researchQuestions",
      "draft_payload_invalid",
      worded("draft_payload_invalid", "Questionnaire draft researchQuestions must be an array."),
    );
  }

  const testedAssets = isRecord(payload.testedAssets)
    ? payload.testedAssets
    : {};
  const { assetTypes: declaredAssetTypes, assetsById } = validateTestedAssets(
    issues,
    testedAssets,
    seenStableKeys,
  );
  const matrixAssetContext: MatrixAssetContext = {
    declaredAssetTypes,
    testedAssets: asArray(testedAssets.assets).filter(isRecord),
  };
  // Option labels compare as respondents read them, in the study's language.
  const wording = respondentWordingFor(
    typeof payload.contentLanguage === "string" ? payload.contentLanguage : undefined,
  );

  const compositeMetricIds = collectCompositeMetricIds(payload);
  const storedQuestions = asArray(isRecord(stored) ? stored.researchQuestions : []);
  const catalog = {
    keys: asArray(testedAssets.assets).flatMap((asset) =>
      isRecord(asset) && hasText(asset.stableKey) ? [asset.stableKey] : [],
    ),
    names: asArray(testedAssets.assets).flatMap((asset) =>
      isRecord(asset) && hasText(asset.name) ? [asset.name] : [],
    ),
  };
  researchQuestions.forEach((question, index) => {
    if (!isRecord(question)) {
      addIssue(
        issues,
        `researchQuestions[${index}]`,
        "draft_payload_invalid",
        worded("draft_payload_invalid", "Every research question must be an object."),
      );
      return;
    }

    const questionPath = `researchQuestions[${index}]`;
    collectStableKey(
      issues,
      seenStableKeys,
      question.stableKey,
      `${questionPath}.stableKey`,
    );
    validateQuestion(
      issues,
      question,
      questionPath,
      wording,
      matrixAssetContext,
      assetsById,
    );
    validateTemplatePlaceholders(issues, question, questionPath);
    validateAssetMentions(issues, storedQuestions[index], questionPath, catalog);
    validateRecipeModuleQuestionType(issues, question, questionPath, payload);
    validateAssetBindings(issues, question, questionPath, declaredAssetTypes);
    validateAssetRefs(issues, question, questionPath, assetsById);
    validateChoiceOptionAssetRefs(
      issues,
      isRecord(question.response) ? question.response : {},
      `${questionPath}.response`,
      assetsById,
    );
    validateCompositeBounds(issues, question, questionPath, compositeMetricIds);
    validateOpenEndCoverage(issues, payload, question, questionPath);
    validateQuestionVisibilityRule(
      issues,
      researchQuestions,
      question,
      index,
      questionPath,
      assetsById,
    );
    validateQuestionRoutingRule(
      issues,
      researchQuestions,
      question,
      index,
      questionPath,
      assetsById,
    );
    validateQuestionChoicesFrom(
      issues,
      researchQuestions,
      question,
      index,
      questionPath,
    );
  });

  validateCompositeReferences(issues, payload);
  validateDisallowedLogic(issues, payload, "");
  issues.push(...assetComparisonCoherenceIssues(payload));
  issues.push(...exposureCoherenceIssues(payload));
  issues.push(...testedAssetUnusedIssues(payload));
  issues.push(...recalledReadIssues(payload));
  const recipe = studySpecOf(payload as { studySpec?: StudySpec })?.recorded.recipe;
  issues.push(
    ...heldBackReadIssues(
      researchQuestions,
      (recipe ? recipeDeclarations(recipe) : null)?.brandUndisclosedModuleIds ?? [],
    ),
  );
  issues.push(...answerabilityIssues(payload));
  issues.push(...reportingCoherenceIssues(payload));

  const hasError = issues.some((issue) => issue.severity === "error");
  return {
    status: hasError ? "invalid" : "valid",
    issues,
  };
}

export function validateQuestionnaireDraft(
  draft: QuestionnaireDraft,
): DraftValidationResult {
  return validateQuestionnaireDraftPayload(draft);
}

// The stored draft's one decider of each rule below; the model wires shape the
// same facts in their schemas (AUTHOR's WireDurationResponse, revise's enums and
// bounds), and Python's draft model is a reader, never a second verdict (wave H T3).
ruleImpl("duration_unit_required", validateQuestion);
ruleImpl("unsupported_duration_unit", validateQuestion);
ruleImpl("study_objective_too_many_points", studyObjectiveIssues);
ruleImpl("design_brief_method_too_long", designBriefIssues);
ruleImpl("design_brief_line_too_long", designBriefIssues);
ruleImpl("study_name_too_long", designBriefIssues);
ruleImpl("situational_context_too_long", designBriefIssues);
ruleImpl("visibility_rule_malformed", conditionExprIssues);
ruleImpl("duplicate_asset_stable_key", validateTestedAssets);
ruleImpl("asset_required_field_missing", validateTestedAssets);
ruleImpl("question_stem_glued", validateQuestion);
ruleImpl("response_kind_mismatch", validateQuestion);
// Facts code writes before this check reads them: a binding's families are
// stamped from the catalog, a carried list's answers are copied from its
// source (the applier's deriveCarriedChoices).
ruleImpl("asset_binding_type_missing", validateAssetBindings);
ruleImpl("choices_from_option_metadata_mismatch", validateQuestionChoicesFrom);

// Each rule below is decided by the function named (wave H T3, move 11).
ruleImpl('asset_description_too_long', validateTestedAssets);
ruleImpl('asset_image_gcs_reference_required', validateTestedAssets);
ruleImpl('asset_media_invalid', validateTestedAssets);
ruleImpl('asset_shared_context_invalid', validateTestedAssets);
ruleImpl('asset_video_not_keyframeable', validateTestedAssets);
ruleImpl('asset_ref_missing', validateAssetRefs);
ruleImpl('asset_ref_stable_key_mismatch', validateAssetRefs);
ruleImpl('asset_ref_type_mismatch', validateAssetRefs);
ruleImpl('choices_from_cardinality_unreachable', validateQuestionChoicesFrom);
ruleImpl('composite_bounds_required', validateCompositeBounds);
ruleImpl('composite_has_no_live_components', validateCompositeReferences);
ruleImpl('composite_metric_ref_missing', validateCompositeReferences);
ruleImpl('continuous_precision_invalid', validateQuestion);
ruleImpl('matrix_measure_label_missing', validateQuestion);
ruleImpl('matrix_measures_mixed_units', validateQuestion);
ruleImpl('matrix_measures_too_few', validateQuestion);
ruleImpl('matrix_rows_placeholder', validateQuestion);
ruleImpl('invalid_routing_rule', routingRuleIssues);
ruleImpl('routing_rule_noop_target', routingRuleIssues);
ruleImpl('lane_exclusion_same_as_gate', validateQuestionVisibilityRule);
ruleImpl('matrix_asset_type_undeclared', validateMatrixRows);
ruleImpl('matrix_rows_no_matching_assets', validateMatrixRows);
ruleImpl('respondent_text_template_placeholder', validateTemplatePlaceholders);
ruleImpl('message_text_required', missingAssetContent);
ruleImpl('not_applicable_label_invalid', validateNotApplicableLabel);
ruleImpl('not_applicable_label_without_option', validateNotApplicableLabel);
ruleImpl('open_end_qual_missing', validateOpenEndCoverage);
ruleImpl('open_end_slides_missing', validateOpenEndCoverage);
ruleImpl('option_label_duplicate', validateChoiceResponse);
ruleImpl('point_label_empty', validateRatingPointLabels);
ruleImpl('point_labels_legacy_scale', validateRatingPointLabels);
ruleImpl('question_recipe_module_type_mismatch', validateRecipeModuleQuestionType);
ruleImpl('study_objective_too_long', studyObjectiveIssues);
ruleImpl('visibility_rule_rank_k_out_of_bounds', conditionExprIssues);
ruleImpl('visibility_rule_rank_bottom_variable_length', conditionExprIssues);
ruleImpl('visibility_rule_threshold_invalid', conditionExprIssues);
ruleImpl('visibility_rule_threshold_out_of_bounds', conditionExprIssues);
ruleImpl('visibility_rule_unknown_matrix_measure', conditionExprIssues);
ruleImpl('visibility_rule_unknown_matrix_row', conditionExprIssues);
ruleImpl('visibility_rule_unknown_value', conditionExprIssues);
ruleImpl('visibility_rule_values_missing', conditionExprIssues);
ruleImpl('held_back_read_after_reveal', heldBackReadIssues);
ruleImpl('held_back_fact_not_told', heldBackReadIssues);
